/**
 * DOM HUD.
 *
 * All text UI lives in the DOM (crisp at any DPI, trivial to restyle); the Pixi
 * canvas underneath is purely the world. Every icon is hand-drawn pixel art
 * generated at boot — no emoji, no image files.
 */

import {
  MAP_H, MAP_W, Phase, PlayerKind, RES_COUNT, RES_KEY, RES_NAME, Res, RoomMode, STRUCTURE_DEFS,
  StructureType, StructureDef, BUILDING_KIND_ICON, BUILDING_KIND_NAME, buildingKind,
  TEAM_DEFS, TEAM_WIN_SHARE, TERRAIN_NAME, Terrain, UNIT_DEFS, UnitType, MAX_STRUCTURE_LEVEL,
  AID_MAX_PER_RESOURCE, AURA_POWER_PER_LEVEL, AURA_RADIUS_PER_LEVEL,
  FOOD_COMFORT_MAX, FOOD_COMFORT_SECONDS, GROWTH_PEAK_AT,
  POP_HOUSING_PER_POINT, REINFORCE_COOLDOWN,
  TROOP_GROWTH_VILLAGE_CAP, UNIT_COST_STEP, UNIT_FLAG_SPARE_BUILDINGS, UNLIMITED,
  UPGRADE_SNAP_RADIUS, WRONG_TERRAIN_FACTOR,
  GATE_HP, RACE_POWERS, RACE_POWER_AREA_MAX, RACE_POWER_AREA_MIN, RACE_POWER_RANGE,
  RACE_POWER_STEP,
  WALL_HP, WALL_PRICE_BLOCK, WALL_PRICE_STEP, WALL_TILES_PER_SEGMENT,
  WallKind,
  LIGHT_SUFFIX, LOBBY_ROTATION, PING_COLOR, ROOM_MODE_NAME, lobbyModeAt, lobbySecondsLeft,
  Race, manaInEdition, racesInEdition, structureInEdition, unitInEdition,
  buildingCost, isCivilianUnit, levelCost, preferredTerrainFor, priceWithGold, racePowerCost,
  racePowerShots, siegePower, structureIncome, terrainFactor, unitCost, unitPopCost, wallCost,
} from '../shared/constants';
import { RACE_DEFS } from '../shared/constants';
import type { UnitDef } from '../shared/constants';
import { formatTime, hexString, shortNumber } from '../shared/util';
import { syncAds } from './ads';
import type { AdSurface } from './ads';
import { anchorFor } from './anchors';
import * as portal from './crazygames';
import { net } from './net';
import type { RoomOpts } from './net';
import { FULLSCREEN_ALLOWED, IS_TOUCH, ON_CRAZYGAMES, TOUCH_ECHO_MS, apiUrl, touchEcho } from './platform';
import { ico, iconUrl } from './render/icons';
import { frameUrl, racePortraitUrl } from './render/sprites';
import { sfx } from './sound';
import type { ClientStruct, ClientUnit, ClientWall } from './state';
import { atWarWith, isFriendly, relationColorTag, state, you } from './state';
import { pageH, pageW } from './viewport';

const $ = (id: string) => document.getElementById(id)!;

export const hooks: {
  cameraTile: () => { x: number; y: number };
  focusTile: (x: number, y: number, zoom?: number) => void;
} = {
  cameraTile: () => ({ x: 0, y: 0 }),
  focusTile: () => { },
};

/**
 * Take the camera to a kingdom's heartland.
 *
 * **Its heartland, not its centroid.** A realm that has swallowed a neighbour is
 * a ring, and the average of a ring is the hole in the middle of it — so "look
 * at their lands" used to fly you to somebody else's capital. `anchorFor` is the
 * same point the kingdom's name is written at, which is by construction inside
 * the largest ground it actually holds. See `client/anchors.ts`.
 *
 * The zoom is chosen from how big the realm is, so a rump fills the screen and
 * an empire fits on it. Without that, clicking a two-tile holdout at map scale
 * moved the camera to a spot where there was still nothing to see.
 */
export function focusKingdom(id: number) {
  const p = state.players.get(id);
  if (!p || p.count <= 0) return;
  const a = anchorFor(id);
  const x = a ? a.x : p.sumX / p.count;
  const y = a ? a.y : p.sumY / p.count;
  // roughly "its main blob spans a third of the screen", clamped to sane travel
  const span = Math.sqrt(Math.max(16, a?.blob ?? p.count));
  hooks.focusTile(x, y, Math.max(0.35, Math.min(3, 26 / span)));
}

/**
 * When the wheel last opened, and how long a click is ignored afterwards.
 *
 * A touch screen fires a synthesised `click` shortly after the `pointerup` that
 * opened the wheel, on the canvas underneath it — which the outside-click
 * handler read as "dismiss". Long enough to cover that, far too short for a
 * player to have chosen a slice and changed their mind.
 */
let radialOpenedAt = -1e9;
const RADIAL_CLICK_GRACE_MS = 350;

/**
 * When a pointer was last seen being lifted *on the wheel itself*.
 *
 * The wheel opens under the fingertip, so the phantom click a browser sends
 * after a touch lands on the menu that tap has just created — on your own
 * ground, squarely on the middle of it, whose only job is to close the menu
 * again. Time cannot tell that click from a real one: both arrive milliseconds
 * after a touch. What tells them apart is that a real press has a pointer
 * behind it that came down and went up *here*, and the ghost has none.
 */
let radialPressAt = -1e9;

let pingMode = -1;
export const getPingMode = () => pingMode;
export const setPingMode = (v: number) => { pingMode = v; renderPingRow(); };

/**
 * Whether this world's war has been decided.
 *
 * Kept here rather than on `state` because it is a fact about what this client
 * has been *shown*, not about the simulation: the standings arrive once, and
 * the button that reopens them has to outlive the panel being dismissed.
 */
let warOver = false;

/* ------------------------------------------------------------- screens */

/**
 * Where advertising is allowed to appear: the menu, the lobby, and the two
 * panels that mark the end of somebody's war.
 *
 * Deliberately derived from what is actually on screen rather than tracked in a
 * variable of its own — the defeat panel comes and goes from `renderDefeat`,
 * the standings from three different buttons, and a second source of truth for
 * "is a panel up" would only drift from the first.
 *
 * Everything here is a moment when nobody is doing anything: reading the room
 * list, waiting out the countdown, looking at the final standings. The war
 * itself is the one place with nothing — the server simulates straight through
 * it at ten ticks a second, so there is no break in there to sell.
 */
function adSurfaceVisible(): AdSurface {
  // checked in front-to-back order: the defeat and results panels sit *over*
  // the war, so they win over whatever screen is underneath them
  if (!$('defeat').classList.contains('hidden')) return 'full';
  if (!$('results').classList.contains('hidden')) return 'full';
  if (!$('lobby').classList.contains('hidden')) return 'full';
  // the menu carries the skyscrapers only — see AdSurface
  if (!$('menu').classList.contains('hidden')) return 'sides';
  return 'none';
}

export function showScreen(which: 'menu' | 'lobby' | 'game') {
  $('menu').classList.toggle('hidden', which !== 'menu');
  $('lobby').classList.toggle('hidden', which !== 'lobby');
  $('hud').classList.toggle('hidden', which !== 'game');
  if (ON_CRAZYGAMES) {
    // the portal wants to know when a player is actually playing, so it can
    // keep out of the way; and a room nobody is in is a room nobody should be
    // invited to, so the invite button goes down with the lobby
    if (which === 'game') portal.gameplayStart(); else portal.gameplayStop();
    if (which === 'menu') portal.setRoom('');
  }
  if (which !== 'game') {
    state.wallMode = 0;
    state.wallDrag.length = 0;
    // the standings belong to a world; leaving it takes them with you
    $('results').classList.add('hidden');
    $('result-reopen').classList.add('hidden');
    // leaving the world clears the personal defeat state with it
    state.defeated = false;
    state.spectating = false;
    $('defeat').classList.add('hidden');
    $('hover-card').classList.add('hidden');
    $('realm-strip').classList.add('hidden');
    $('watch-bar').classList.add('hidden');
    warOver = false;
    document.body.classList.remove('spectating');
    closeRadial();
    closeAidPanel();
    // kingdom ids are handed out again in the next war, so a leftover here
    // would tell somebody to wait for a gift they never sent
    lastReinforceAt.clear();
  }
  syncAds(adSurfaceVisible());
}

/**
 * The way out, for anyone who is only watching now.
 *
 * Shown once you are dead and have chosen to keep watching, or once the war has
 * been decided — the two states where every panel can be dismissed and the map
 * is all that is left. Without it, "keep watching" was a one-way door.
 */
function renderWatchBar() {
  const watching = (state.defeated && state.spectating) || warOver;
  $('watch-bar').classList.toggle('hidden', !watching);
}

/* ------------------------------------------------------ server browser */

let joinHandler: (name: string, room: string, create: boolean, opts?: RoomOpts) => void = () => { };
let browserTimer: number | null = null;
let clockTimer: number | null = null;
/** the last poll's rows, so the clocks can tick between polls */
let lastRooms: any[] = [];
let lastRoomsAt = 0;

/** Polls the server browser while the menu is on screen. */
async function refreshRooms() {
  const list = $('room-list');
  let rooms: any[] = [];
  try {
    const res = await fetch(apiUrl('/rooms'), { cache: 'no-store' });
    rooms = (await res.json()).rooms || [];
  } catch {
    list.innerHTML = '<div class="room-empty">Cannot reach the server.</div>';
    return;
  }
  /*
   * Only the worlds of the edition you are in.
   *
   * The two never share a match, so showing both would be offering rooms that
   * are not the game on your screen — and a beginner clicking a full world
   * would land among elves and mana with no idea why.
   */
  rooms = rooms.filter(r => Boolean(r.light) === state.light);

  /*
   * **A war in progress is not on offer, and is not listed.**
   *
   * Somebody else's match is nothing a new arrival can do anything with: they
   * cannot join it, so a row saying "in progress" is a locked door with a sign
   * on it — and on a busy server there are more locked doors than open ones,
   * which buries the one world they actually came for.
   *
   * The exception is **your own**. A dropped connection, a closed tab, a
   * crashed browser: your kingdom is still standing there asleep waiting for
   * you (see `Room.sleepingKingdom`), and this is how you find your way back
   * to it. Only you see it, because only your browser remembers the code.
   */
  const mine = localStorage.getItem('kaelmoor.room') || '';
  const rejoin = mine ? rooms.find(r => r.id === mine && r.phase >= Phase.Spawn) : undefined;
  rooms = rooms.filter(r => r.phase < Phase.Spawn);
  if (rejoin) rooms.unshift({ ...rejoin, rejoin: true });

  if (rooms.length === 0) {
    list.innerHTML = '<div class="room-empty">No worlds yet — start one below.</div>';
    return;
  }
  lastRooms = rooms;
  lastRoomsAt = performance.now();

  list.innerHTML = '';
  for (const r of rooms) {
    const btn = document.createElement('button');
    btn.className = `room-row${r.mode === RoomMode.FreeForAll ? ' ffa' : ''}`
      + (r.mode === RoomMode.VersusAi ? ' vsai' : '');
    btn.dataset.room = r.id;
    btn.innerHTML = roomRowHtml(r, 0);
    btn.addEventListener('click', () => join(r.id, false));
    list.appendChild(btn);
  }
}

/**
 * One row of the server browser, `elapsed` seconds after the poll that produced
 * it — so the clock counts down every second instead of jumping every 2.5.
 */
function roomRowHtml(r: any, elapsed: number): string {
  const live = r.phase >= Phase.Spawn;
  const full = r.humans >= r.max;
  /*
   * The open lobby's clock is read straight off the wall clock, the same one
   * the server uses — polling every 2.5 s and ticking down in between is fine
   * for a number that only falls, and wrong at the second the turn wraps back
   * to sixty. Every other kind of room still counts down from what it reported.
   */
  const left = r.rotating
    ? Math.ceil(lobbySecondsLeft(Date.now()))
    : Math.max(0, Math.round(r.countdown - elapsed));

  /*
   * An empty *private* world's clock is parked until somebody walks in; showing
   * a frozen countdown to a room that will not move is a countdown that never
   * counts. The open lobby is different — its clock runs whether anybody came
   * or not, so it always shows the real number.
   */
  const status = r.rejoin ? '<span class="status soon">your war — <b>rejoin</b></span>'
    : full ? '<span class="status full">full</span>'
      : live ? '<span class="status live">in progress</span>'
        // a private room has no clock at all: it waits for the person who opened it
        : r.manualStart ? '<span class="status wait">host starts it</span>'
          : r.waiting ? '<span class="status wait">waiting for players</span>'
            : `<span class="status soon">starts in <b>${left}s</b></span>`;

  const shape = r.mode === RoomMode.FreeForAll ? 'every kingdom for itself'
    : r.mode === RoomMode.VersusAi ? 'everyone against the machines'
      : `${r.teams} teams`;
  const world = r.worldW ? ` · map ${r.worldW}×${r.worldH}` : '';
  /*
   * What comes after this one.
   *
   * Only one mode is open at a time, so without this the other two look like
   * they have been removed rather than like they are next in line — and
   * somebody who came to play teams needs to know whether to wait a minute or
   * go away.
   */
  const nextUp = r.rotating
    ? ` · <span class="next">then ${ROOM_MODE_NAME[LOBBY_ROTATION[
      (LOBBY_ROTATION.indexOf(lobbyModeAt(Date.now())) + 1) % LOBBY_ROTATION.length]]}</span>`
    : '';

  /*
   * **The count is the row.**
   *
   * This used to be one line of twelve-pixel prose in which "0 / 72 players"
   * was the third thing after the mode name and read no louder than the camp
   * count. But the only question somebody scanning this list is asking is *is
   * anybody in there* — so the count is now the biggest thing on the row, on
   * its own line with the mode and the clock, above a meter you can read from
   * across the desk. Everything else is one dim line underneath.
   *
   * The room code goes on that line only for private worlds, where it is how a
   * friend gets in. On the public lobby it is a random string nobody types.
   */
  const pct = Math.min(100, (r.humans / Math.max(1, r.max)) * 100);
  const code = r.rotating ? '' : ` · <small>${escapeHtml(r.id)}</small>`;

  return '<span class="rmain">' +
    '<span class="rtop">' +
    `<span class="mode">${escapeHtml(r.modeName || r.id)}</span>` +
    `<span class="count"><b>${r.humans}</b><i>/${r.max}</i></span>` +
    status +
    '</span>' +
    `<span class="meter"><i style="width:${pct}%"></i></span>` +
    `<span class="sub">${shape} · ${r.aiKingdoms} AI · ${r.camps} camps${world}${nextUp}${code}</span>` +
    '</span>';
}

/** Re-renders the rows once a second so the countdowns actually move. */
function tickRoomClocks() {
  if (lastRooms.length === 0) return;
  const elapsed = (performance.now() - lastRoomsAt) / 1000;
  for (const r of lastRooms) {
    const btn = document.querySelector(`.room-row[data-room="${r.id}"]`);
    if (btn) btn.innerHTML = roomRowHtml(r, elapsed);
  }
}

function join(room: string, create: boolean, opts?: RoomOpts) {
  const nameInput = $('name-input') as HTMLInputElement;
  const name = nameInput.value.trim();
  localStorage.setItem('kaelmoor.name', name);
  if (browserTimer !== null) { clearInterval(browserTimer); browserTimer = null; }
  if (clockTimer !== null) { clearInterval(clockTimer); clockTimer = null; }
  joinHandler(name, room, create, opts);
}

/* ------------------------------------------------------- your own world */

/**
 * The create form.
 *
 * Opening a world of your own used to mean taking whatever the matchmaker
 * happened to roll — a 96-slot team world for three friends. Everything here is
 * clamped again on the server (`roomConfigFrom`), so the form is a convenience
 * and never a source of truth.
 */
function createOpts(): RoomOpts {
  const num = (id: string) => Number(($(id) as HTMLInputElement).value);
  const mode = Number(document.querySelector('#create-mode button.on')?.getAttribute('data-mode') ?? RoomMode.Teams);
  return {
    mode,
    // a world you open yourself is the edition you are playing
    light: state.light,
    maxPlayers: num('create-max'),
    teams: num('create-teams'),
    aiKingdoms: num('create-ai'),
    camps: num('create-camps'),
  };
}

function refreshCreatePanel() {
  const o = createOpts();
  $('create-max-val').textContent = String(o.maxPlayers);
  $('create-teams-val').textContent = String(o.teams);
  $('create-ai-val').textContent = String(o.aiKingdoms);
  $('create-camps-val').textContent = String(o.camps);
  // a free-for-all has no teams to split into, and VS AI has exactly two sides
  $('create-teams-row').classList.toggle('hidden', o.mode !== RoomMode.Teams);
  const shape = o.mode === RoomMode.FreeForAll ? 'every kingdom for itself'
    : o.mode === RoomMode.VersusAi ? `everyone against ${o.aiKingdoms} machine kingdoms`
      : `${o.teams} teams — everybody picks a side in the lobby`;
  $('create-note').textContent =
    `${o.maxPlayers} slots · ${shape} · ${o.camps} monster camps. ` +
    'It waits for you to start it, so take as long as you like.';
}

function initCreatePanel() {
  const panel = $('create-panel');
  $('new-room-btn').addEventListener('click', () => {
    panel.classList.toggle('hidden');
    refreshCreatePanel();
    sfx.play('click');
  });
  $('create-cancel').addEventListener('click', () => panel.classList.add('hidden'));
  for (const btn of document.querySelectorAll('#create-mode button')) {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#create-mode button').forEach(b => b.classList.remove('on'));
      btn.classList.add('on');
      refreshCreatePanel();
    });
  }
  for (const id of ['create-max', 'create-teams', 'create-ai', 'create-camps']) {
    $(id).addEventListener('input', refreshCreatePanel);
  }
  $('create-go').addEventListener('click', () => join('', true, createOpts()));
  refreshCreatePanel();
}

export function startRoomBrowser() {
  refreshRooms();
  if (browserTimer === null) browserTimer = window.setInterval(refreshRooms, 2500);
  if (clockTimer === null) clockTimer = window.setInterval(tickRoomClocks, 1000);
}

/**
 * **Hang a picture of a live war behind the menu.**
 *
 * Set once at start-up and refreshed on the same cadence the server repaints
 * on, so a player sitting in the lobby watches the borders of a real match move
 * every half-minute. `?t=` because the response is cached deliberately — the
 * only way to ask for the *next* picture is to ask for a different URL.
 *
 * Failure is silence: if the server has no live world to photograph it answers
 * 503, the variable stays unset, and the gradients underneath are the menu they
 * always were. A backdrop is not worth an error message.
 */
function hangBackdrop() {
  const url = apiUrl(`/backdrop.png?t=${Math.floor(Date.now() / BACKDROP_REFRESH_MS)}`);
  const img = new Image();
  img.onload = () => document.documentElement.style.setProperty('--backdrop', `url("${url}")`);
  img.src = url;
}
const BACKDROP_REFRESH_MS = 25_000;

/**
 * Default the kingdom name to the portal username, for a signed-in visitor.
 *
 * **Prefilled, never forced.** The field stays exactly as editable as it is on
 * kaelmoor.io, and somebody who is not signed in — or whose portal is having a
 * bad day — types a name the way they always have. That is the whole of the
 * requirement: the portal wants its usernames visible in game so friends can
 * recognise each other, and a kingdom name is where a name is visible.
 *
 * A name already in the field wins. It came out of `localStorage`, which means
 * this player has chosen one before, and a returning player's own choice is
 * worth more than a default.
 */
async function prefillPortalName(field: HTMLInputElement) {
  const name = await portal.portalUsername();
  if (!name || field.value.trim()) return;
  field.value = name;
}

/*
 * The menu's root-absolute links used to be rewritten here, at boot. They are
 * fixed in `index.html` at build time instead — see `portalHtml` in
 * vite.config.ts. A link repaired by JavaScript is still the wrong link in the
 * file somebody reviews, and briefly the wrong link in the page.
 */

export function initUI(onPlay: (name: string, room: string, create: boolean, opts?: RoomOpts) => void) {
  joinHandler = onPlay;
  hangBackdrop();
  setInterval(hangBackdrop, BACKDROP_REFRESH_MS);
  const nameInput = $('name-input') as HTMLInputElement;
  const roomInput = $('room-input') as HTMLInputElement;
  nameInput.value = localStorage.getItem('kaelmoor.name') || '';
  const params = new URLSearchParams(location.search);
  if (params.get('room')) roomInput.value = params.get('room')!;
  if (ON_CRAZYGAMES) {
    prefillPortalName(nameInput);
    /*
     * An invite link is the same gesture as `?room=CODE`, arriving by the
     * portal's route instead of ours. Read after the query string so that a
     * player who was genuinely invited lands in the room they were invited to.
     */
    const invited = portal.invitedRoom();
    if (invited) roomInput.value = invited;
  }

  // typing a code and pressing enter joins (or creates) that world directly
  roomInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && roomInput.value.trim()) join(roomInput.value.trim().toUpperCase(), false);
  });
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') join(roomInput.value.trim().toUpperCase(), false);
  });
  // the same thing the Enter key does, for everyone who does not guess at keys
  $('join-btn').addEventListener('click', () => {
    const code = roomInput.value.trim().toUpperCase();
    if (!code) {
      notice('Type a room code first — or pick a world from the list below.', 'bad');
      sfx.play('error');
      return;
    }
    join(code, false);
  });
  initCreatePanel();
  startRoomBrowser();
  buildRacePickers();
  initSettings();
  initFullscreen();
  initDefeat();
  initResults();
  initSquadRow();
  initRealmRow();
  initLobbyStart();
  restoreFolds();

  const ratio = $('ratio') as HTMLInputElement;
  ratio.addEventListener('input', () => {
    state.ratio = Number(ratio.value) / 100;
    $('ratio-label').textContent = `${ratio.value}%`;
  });

  for (const btn of document.querySelectorAll('#ping-row button')) {
    btn.addEventListener('click', () => {
      const kind = Number((btn as HTMLElement).dataset.ping);
      pingMode = pingMode === kind ? -1 : kind;
      renderPingRow();
    });
  }
  // colour them now rather than on the first HUD tick: until a match starts
  // nothing calls `updateHUD`, and three grey buttons is not what they are
  renderPingRow();

  /*
   * **The wheel takes a press it watched arrive, and refuses the rest.**
   *
   * Both halves are needed. The `pointerup` half is the witness: a genuine tap
   * lifts a touch pointer on the slice, and a genuine mouse click lifts a mouse
   * pointer at a moment when no finger has just been on the glass. The `click`
   * half is the door: a click with no such witness behind it is the browser
   * replaying the touch that opened this menu, and it is stopped here rather
   * than at each of the twenty buttons it might otherwise press.
   *
   * Capture phase, so the ghost never reaches a slice — and `stopPropagation`
   * with it, so it does not reach the dismiss handler below either. A ghost
   * should do nothing at all, and closing the wheel is not nothing.
   */
  const radialEl = $('radial');
  radialEl.addEventListener('pointerup', (e) => {
    if ((e as PointerEvent).pointerType === 'touch' || !touchEcho()) {
      radialPressAt = performance.now();
    }
  }, true);
  radialEl.addEventListener('click', (e) => {
    if (!IS_TOUCH) return;
    if (performance.now() - radialPressAt < TOUCH_ECHO_MS) return;
    e.stopPropagation();
    e.preventDefault();
  }, true);

  document.addEventListener('click', (e) => {
    const popup = $('diplo-popup');
    if (!popup.contains(e.target as Node)) popup.classList.add('hidden');
    /*
     * **The click that opened the wheel must not be the click that closes it.**
     *
     * On a touch screen the browser synthesises a `click` a moment after the
     * `pointerup` that opened the wheel, and it lands on the canvas — outside
     * the wheel — so the menu appeared and vanished in the same frame. A short
     * grace window after opening is the fix that does not require guessing
     * which events are synthetic; it also covers a desktop right-click that
     * happens to be followed by a stray click.
     */
    if (performance.now() - radialOpenedAt < RADIAL_CLICK_GRACE_MS) return;
    /*
     * On a touch screen this handler is not the one that dismisses the wheel —
     * `input.ts` does it from the tap itself, on `pointerup`, where it can tell
     * a tap on the map from a replayed one. Every click a finger produces
     * arrives inside the echo window, so answering them here as well is how a
     * menu came to be closed twice for one gesture.
     */
    if (touchEcho()) return;
    if (!$('radial').contains(e.target as Node)) closeRadial();
  });

  buildPalettes();
  // last, because it rebuilds the pickers and the palette for whichever
  // edition was remembered — and on a first visit that is Light
  initEditionPick();
}

/* ------------------------------------------------------------- editions */

/**
 * **Which edition actually governs right now.**
 *
 * In a lobby or a match it is the *world's* — the room decided its edition when
 * it opened and no client preference overrides it. On the menu, before there is
 * a world, it is what you have chosen. Everything that asks "does this exist"
 * goes through here so the two can never disagree on screen.
 */
export function editionInPlay(): boolean {
  return state.connected ? state.lobby.light : state.light;
}

/**
 * The Light / Full switch, and everything on the menu that follows from it.
 *
 * Switchable at any time and remembered, because the whole point is that Light
 * is a place to start rather than a different game: somebody who has learned
 * the borders moves across and keeps everything they know.
 */
function applyEdition() {
  const light = state.light;
  localStorage.setItem('kaelmoor.light', light ? '1' : '0');

  $('edition-tag').classList.toggle('hidden', !light);
  document.title = `Kaelmoor.io${light ? LIGHT_SUFFIX : ''} — fantasy conquest`;

  for (const btn of document.querySelectorAll('#edition-pick button')) {
    btn.classList.toggle('on', (btn.getAttribute('data-light') === '1') === light);
  }

  /*
   * In Light there is one people, so the whole step goes — head and all.
   *
   * Hiding the head as well as the picker is what lets the step numbers stay
   * honest: the counter runs over `.step-head` boxes, and a hidden one is not a
   * box, so Light reads 1, 2 rather than 1, 3.
   */
  $('race-picker').classList.toggle('hidden', light);
  $('race-head').classList.toggle('hidden', light);
  if (light) pickRace(Race.Human);

  buildRacePickers();
  applyRaceTheme();
  buildPalettes();
  refreshRooms();
}

function initEditionPick() {
  for (const btn of document.querySelectorAll('#edition-pick button')) {
    btn.addEventListener('click', () => {
      state.light = btn.getAttribute('data-light') === '1';
      sfx.play('click');
      applyEdition();
    });
  }
  applyEdition();
}

/* --------------------------------------------------------- race picker */

function buildRacePickers() {
  for (const id of ['race-picker', 'lobby-race']) {
    const host = document.getElementById(id);
    if (!host) continue;
    host.innerHTML = '';
    // a Light world fields one people, so there is no choice to offer
    RACE_DEFS.forEach((def, race) => {
      if (!racesInEdition(editionInPlay()).includes(race as Race)) return;
      const btn = document.createElement('button');
      btn.className = 'race-card';
      btn.dataset.race = String(race);
      /*
       * A face, a name, two words — and the rules only if you ask for them.
       *
       * `rdesc` used to sit open on the card, which put three paragraphs on the
       * menu and made picking a race feel like homework. It is now a tooltip
       * that appears on hover or keyboard focus, so the card stays a picture
       * with a name on it and the detail is one mouse-move away for anybody who
       * wants it. Nothing is hidden — it just is not shouted.
       */
      btn.innerHTML =
        // 144 rasterises the 24-square grid at a whole 5 pixels a cell and the
        // card shows it at 72 — an exact halving, so nothing is resampled
        `<img class="rpic" src="${racePortraitUrl(race, 144)}" alt="" draggable="false" />` +
        `<span class="rname">${def.name}</span>` +
        `<span class="rtag">${def.tagline}</span>` +
        `<span class="rdesc"><b>${def.name}</b>${def.desc}</span>`;
      btn.addEventListener('click', () => pickRace(race));
      host.appendChild(btn);
    });
  }
  refreshRacePickers();
}

function pickRace(race: number) {
  state.race = race;
  localStorage.setItem('kaelmoor.race', String(race));
  // in a lobby the server has to agree; in the menu it travels with the hello
  if (state.connected && state.phase === Phase.Lobby) net.send({ t: 'race', race });
  refreshRacePickers();
  applyRaceTheme();
  buildPalettes();
  sfx.play('click');
}

/**
 * **Dress the menu and the lobby in the chosen people's colours.**
 *
 * Two halves that must not drift apart: `data-race` on the body, which the
 * stylesheet reads to swap the accent variables, and the carved frame, which is
 * a `border-image` and therefore has to be written from script because its
 * source is drawn at boot rather than shipped as a file.
 *
 * Called from `pickRace` and `applyEdition` — every path that can change which
 * people you are, including the Light edition forcing Humans.
 */
function applyRaceTheme() {
  const race = state.race;
  document.body.dataset.race = String(race);
  const { url, slice } = frameUrl(race);
  for (const sel of ['.menu-panel', '.lobby-panel']) {
    const panel = document.querySelector(sel) as HTMLElement | null;
    if (!panel) continue;
    panel.classList.add('framed');
    // width first: the border must exist before an image can be laid into it
    panel.style.borderWidth = `${slice / 2}px`;
    panel.style.borderImageSource = `url(${url})`;
    panel.style.borderImageSlice = String(slice);
    panel.style.borderImageRepeat = 'round';
    panel.style.borderImageWidth = `${slice / 2}px`;
  }
}

function refreshRacePickers() {
  for (const el of document.querySelectorAll('.race-card')) {
    el.classList.toggle('on', Number((el as HTMLElement).dataset.race) === state.race);
  }
}

/* ------------------------------------------------------------ settings */

function initSettings() {
  const panel = $('settings-panel');
  const music = $('music-vol') as HTMLInputElement;
  const sfxVol = $('sfx-vol') as HTMLInputElement;

  music.value = String(Math.round(sfx.musicVolume * 100));
  sfxVol.value = String(Math.round(sfx.sfxVolume * 100));
  $('music-val').textContent = music.value;
  $('sfx-val').textContent = sfxVol.value;

  music.addEventListener('input', () => {
    sfx.setMusicVolume(Number(music.value) / 100);
    $('music-val').textContent = music.value;
  });
  sfxVol.addEventListener('input', () => {
    sfx.setSfxVolume(Number(sfxVol.value) / 100);
    $('sfx-val').textContent = sfxVol.value;
    sfx.play('click');
  });

  $('settings-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    panel.classList.toggle('hidden');
  });
  $('close-settings').addEventListener('click', () => panel.classList.add('hidden'));
}

export function toggleSettings() {
  $('settings-panel').classList.toggle('hidden');
}

/* ---------------------------------------------------------- full screen */

/**
 * A conquest map wants the whole screen. The browser also exits full screen on
 * Esc by itself, so all we have to do is keep our own button in step with
 * whatever state the browser ends up in.
 *
 * **Except inside a portal frame.** CrazyGames supplies the frame and its own
 * full-screen control, and their requirements prohibit a custom in-game one —
 * two controls fighting over the same state is a worse experience than one, and
 * an iframe will in any case refuse the request unless the embedder allowed it.
 * `FULLSCREEN_ALLOWED` folds to a literal, so in that build these functions are
 * empty and the `G` key and the ⛶ button are gone rather than broken.
 */
export function toggleFullscreen() {
  if (!FULLSCREEN_ALLOWED) return;
  if (document.fullscreenElement) {
    document.exitFullscreen?.().catch(() => { /* already leaving */ });
  } else {
    document.documentElement.requestFullscreen?.().catch(() => {
      notice('Your browser refused full screen — press F11 instead.', 'bad');
    });
  }
}

export function exitFullscreen() {
  if (!FULLSCREEN_ALLOWED) return;
  if (document.fullscreenElement) document.exitFullscreen?.().catch(() => { /* ignore */ });
}

function initFullscreen() {
  if (!FULLSCREEN_ALLOWED) {
    /*
     * The button is already gone: the build strips it out of `index.html`, so a
     * control that cannot work is absent from the document rather than hidden
     * by CSS. This is the belt to that pair of braces — `?.` and not `$()`,
     * because the element being missing is the expected case here, and `$()`
     * asserts non-null and would throw.
     */
    document.getElementById('fullscreen-btn')?.remove();
    return;
  }
  $('fullscreen-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    toggleFullscreen();
    sfx.play('click');
  });
  document.addEventListener('fullscreenchange', () => {
    document.body.classList.toggle('fullscreen', !!document.fullscreenElement);
  });
}

/* -------------------------------------------------- collapsible panels */

/** Every card header folds its card away, and the choice is remembered. */
function restoreFolds() {
  /*
   * **A separate memory per device shape.**
   *
   * One key meant a phone inherited whatever was unfolded on a desktop — and
   * since the desktop default is "everything open", a player who had ever
   * played on a laptop arrived on their phone with every panel expanded over
   * the map. What is comfortable on one is wrong on the other, so they no
   * longer share an answer.
   */
  const saved = localStorage.getItem(IS_TOUCH ? 'kaelmoor.folded.touch' : 'kaelmoor.folded');
  /*
   * **On a touch screen every panel starts folded.**
   *
   * A phone has a fraction of the screen and all of the same information, and
   * the panels are the part a player can put away — the map is not. Folded is
   * therefore the right *default* rather than a preference to be nagged about;
   * unfolding one is a tap, and the choice is remembered from then on, so this
   * only decides what somebody sees the very first time.
   *
   * On a desktop nothing changes: no saved preference still means everything
   * open.
   */
  const folded = saved === null && IS_TOUCH
    ? null                                   // null = fold everything
    : new Set((saved || '').split(',').filter(Boolean));
  document.querySelectorAll('#hud .card').forEach((card, i) => {
    const head = card.querySelector('.card-head');
    if (!head) return;
    const key = (head.textContent || String(i)).trim().slice(0, 16);
    if (folded === null || folded.has(key)) card.classList.add('folded');
    head.addEventListener('click', () => {
      card.classList.toggle('folded');
      const now = new Set<string>();
      document.querySelectorAll('#hud .card.folded .card-head').forEach(h => {
        now.add((h.textContent || '').trim().slice(0, 16));
      });
      localStorage.setItem(
        IS_TOUCH ? 'kaelmoor.folded.touch' : 'kaelmoor.folded', [...now].join(','));
      sfx.play('click');
    });
  });
}

export function toggleCinematic() {
  document.body.classList.toggle('cinematic');
}

/* ------------------------------------------------------------- tooltip */

function attachTooltip(el: HTMLElement, html: () => string) {
  const tip = $('tooltip');
  el.addEventListener('mouseenter', () => {
    tip.innerHTML = html();
    tip.classList.remove('hidden');
    const r = el.getBoundingClientRect();
    const left = r.right + 10 + 250 > window.innerWidth ? r.left - 260 : r.right + 10;
    tip.style.left = `${Math.max(6, left)}px`;
    tip.style.top = `${Math.min(window.innerHeight - 150, r.top)}px`;
  });
  el.addEventListener('mousemove', () => {
    if (!tip.classList.contains('hidden')) tip.innerHTML = html();
  });
  el.addEventListener('mouseleave', () => tip.classList.add('hidden'));
}

/**
 * A price, drawn to be *read*.
 *
 * Every figure gets its own chunk with its own icon so the eye can separate
 * "110 wood, 30 gold" at a glance instead of parsing a run of digits. The price
 * is the first thing a player looks at on a build button, so it is laid out
 * like a price tag and not like a footnote.
 */
/**
 * Which people a kingdom belongs to, in brackets after its name.
 *
 * Monster camps have no people, so they get nothing. Everyone else is worth
 * labelling: a race decides where its villages want to stand, what its power
 * does, and — since caravans pay 50 % more across peoples — whether it is worth
 * trading with. That is too much to have to remember per neighbour.
 */
function raceTag(p: { race: number; kind: number }): string {
  if (p.kind === PlayerKind.Camp) return '';
  const def = RACE_DEFS[p.race];
  return def ? ` <small class="race-tag">(${def.name})</small>` : '';
}

/**
 * The mark that says what a building is for — economy, military or housing.
 *
 * Placed before the name everywhere a name appears, because twenty-six
 * buildings is more than anybody sorts by memory, and "is that making them
 * money or making them soldiers" is the only question most of them raise.
 * See `buildingKind`.
 */
function kindTag(def: StructureDef): string {
  const kind = buildingKind(def);
  return `<i class="ico kind" title="${BUILDING_KIND_NAME[kind]}" ` +
    `style="background-image:url(${iconUrl(BUILDING_KIND_ICON[kind])})"></i>`;
}

function costHtml(cost: number[], size = 12): string {
  const parts: string[] = [];
  /*
   * Red means "this is the one you are short of".
   *
   * Only marked when the purchase is actually blocked, because gold quietly
   * covers any other shortfall — reddening wood you lack while the treasury is
   * paying for it would be a warning about nothing. When it *is* blocked, every
   * figure you cannot cover goes red, so the reason the button does nothing is
   * on the button.
   */
  const blocked = !affordable(cost);
  cost.forEach((c, r) => {
    if (c <= 0) return;
    const short = blocked && state.self.res[r] < c;
    parts.push(`<span class="c${short ? ' short' : ''}">`
      + `${ico(RES_KEY[r], size)}<b>${Math.round(c)}</b></span>`);
  });
  return parts.join('') || '<span class="c">free</span>';
}

/** Gold covers whatever the stockpiles cannot: shows what a price really costs. */
/*
 * Both of these go through the *shared* price rule, and that is the whole point.
 *
 * They used to be a private reimplementation of it here, and the copy had a
 * hole: it turned a shortfall of **any** resource into gold, while the real
 * rule refuses outright when what you are short of is mana. Gold buys grain,
 * timber and stone; magic has to be mined out of a ley field.
 *
 * The consequence was a button that lied. The Phoenix Aerie wants 200 mana, so
 * with no Ley Shrine the client called it affordable, enabled the button, took
 * the click — and the server quietly refused, over and over, with nothing on
 * screen to say why. Anything that decides whether a player may buy something
 * has to be the same code on both ends.
 */
function goldShortfall(cost: number[]): number {
  return priceWithGold(cost, state.self.res).goldNeeded;
}

function affordable(cost: number[]): boolean {
  return priceWithGold(cost, state.self.res).ok;
}

/**
 * Total **levels** you hold of a building type — the sum, not the count.
 *
 * This is what the next one costs against, whether you take it as a new
 * building or as a level on an old one. Both buy the same thing, so both are
 * charged the same, and this is the number that decides it.
 */
function ownedLevelsOf(type: number): number {
  let levels = 0;
  for (const s of state.structures.values()) {
    if (s.owner === state.you && s.type === type) levels += s.level;
  }
  return levels;
}

/**
 * The price of the next unit of this building type.
 *
 * One function for both paths now: building a second hall and adding a second
 * floor cost exactly the same, so there is nothing left for two functions to
 * disagree about — which is what they used to do, by a factor that flipped
 * direction at the fourth one.
 */
function nextBuildingCost(type: number): number[] {
  return buildingCost(STRUCTURE_DEFS[type], ownedLevelsOf(type));
}

/**
 * What the next wall segment costs. Counted from the figure the server sends,
 * not from `state.walls` — that map only holds what is on screen, and a price
 * that changes when you scroll away from your own border is not a price.
 */
function nextWallCost(gate: boolean): number[] {
  return wallCost(state.self.wallCount, gate);
}

function wallTooltip(kind: number): string {
  const gate = kind === WallKind.Gate;
  const owned = state.self.wallCount;
  const inBlock = owned % WALL_PRICE_BLOCK;
  let html = `<h4>${gate ? 'Gate' : 'Wall'}</h4>`;
  html += gate
    ? '<div class="t-desc">A way through your own wall. It stops conquest exactly like a rampart does, ' +
      'but <b>you and your allies walk through it</b> — and nobody else. Weaker than plain wall: ' +
      'the doors of a fortress are also its soft spots.</div>'
    : '<div class="t-desc">Territory <b>cannot be taken through a standing wall</b>. An army does not ' +
      'harm it at all — an assault that runs into one simply has nowhere left to go. Only <b>trained ' +
      'units</b> pull a wall down. Nothing on foot crosses a rampart either, <b>your own troops ' +
      'included</b>, which is what gates are for.</div>';
  html += '<div class="t-line">Drag the mouse across your own land to draw a line of it</div>';
  html += `<div class="t-line">${Math.round(gate ? GATE_HP : WALL_HP)} HP per segment` +
    ' · nothing may be built on one, and no wall may be drawn under a building</div>';
  html += `<div class="t-line">This one costs ${costHtml(nextWallCost(gate), 11)}</div>`;
  html += `<div class="t-line">You have <b>${owned}</b> standing — every ${WALL_PRICE_BLOCK} segments ` +
    `makes the next ${WALL_PRICE_BLOCK} cost +${Math.round(WALL_PRICE_STEP * 100)} % ` +
    `<small>(${WALL_PRICE_BLOCK - inBlock} left at this price)</small></div>`;
  html += '<div class="t-line t-good">Archers shoot straight over it. Only units break it: a squad of ' +
    'knights in seconds, a dragon or phoenix from above in three passes.</div>';
  // the quota is the thing players will be surprised by, so it goes last and plainly
  const manned = Math.floor((you()?.tiles ?? 0) / WALL_TILES_PER_SEGMENT);
  html += `<div class="t-line ${owned > manned ? 't-bad' : ''}">Somebody has to stand on a wall: ` +
    `your realm mans <b>one segment per ${WALL_TILES_PER_SEGMENT} tiles</b> — ` +
    `<b>${manned}</b> right now. Anything past that is abandoned and crumbles.</div>`;
  return html;
}

/* ------------------------------------------------------------ palettes */

function buildPalettes() {
  /*
   * The build palette is **absent** on a touch screen, not hidden — `markDevice`
   * takes the whole card out of the document. So every reader of it has to
   * tolerate its not being there, rather than asserting with `$()`. The army
   * palette below stays on every device.
   */
  const build = document.getElementById('build-palette');
  if (build) build.innerHTML = '';

  /*
   * Walls come first, because they are the only thing here that is *drawn*
   * rather than placed and the tool has to be findable. They are not
   * structures — see `WallKind` — so they get their own two buttons instead of
   * a row in the table below.
   */
  const wallBtn = build ? (kind: WallKind, key: string, hotkey: string, name: string, blurb: string) => {
    const btn = document.createElement('button');
    btn.className = 'pal-btn wall-btn';
    btn.dataset.wall = String(kind);
    btn.innerHTML =
      `${ico(key, 26)}<span class="txt"><span class="ttl">${name} <kbd>${hotkey}</kbd>` +
      `<small>${blurb}</small></span>` +
      `<span class="cost">${costHtml(wallCost(0, kind === WallKind.Gate))}</span></span>`;
    btn.addEventListener('click', () => toggleWallMode(kind));
    attachTooltip(btn, () => wallTooltip(kind));
    build.appendChild(btn);
  } : () => { /* no palette on a touch screen — the wheel carries the walls */ };
  wallBtn(WallKind.Rampart, 'wall', 'V', 'Wall', 'drag to draw');
  wallBtn(WallKind.Gate, 'gate', 'N', 'Gate', 'your side only');

  STRUCTURE_DEFS.forEach((def, type) => {
    if (!build) return;
    if (type === StructureType.Keep || def.buildable === false) return;
    if (def.race !== undefined && def.race !== state.race) return;   // other peoples' halls
    // and whatever this edition does not contain — the server refuses it anyway,
    // so offering it would only be a button that does nothing
    if (!structureInEdition(type as StructureType, editionInPlay())) return;
    const btn = document.createElement('button');
    btn.className = 'pal-btn';
    btn.dataset.type = String(type);
    // the real opening price, not the raw table row — `refreshPalettes` keeps it
    // current once a match starts, but this is what the menu shows until then
    btn.innerHTML =
      `${ico(def.key, 26)}<span class="txt"><span class="ttl">${kindTag(def)}${def.name}</span>` +
      `<span class="cost">${costHtml(buildingCost(def, 0))}</span></span>`;
    btn.addEventListener('click', () => {
      state.buildType = state.buildType === type ? -1 : type;
      state.trainType = -1;
      state.boatMode = false;
      state.wallMode = 0;
      refreshPalettes();
    });
    attachTooltip(btn, () => structureTooltip(type));
    build.appendChild(btn);
  });

  const train = $('train-palette');
  train.innerHTML = '';
  UNIT_DEFS.forEach((def, type) => {
    if (!def.trainable) return;
    const home = STRUCTURE_DEFS[def.from[0]];
    if (home.race !== undefined && home.race !== state.race) return;
    // a soldier exists where the hall that trains him does, and nowhere else
    if (!unitInEdition(type as UnitType, editionInPlay())) return;
    const btn = document.createElement('button');
    btn.className = 'pal-btn';
    btn.dataset.unit = String(type);
    btn.innerHTML =
      `${ico(def.key, 26)}<span class="txt"><span class="ttl">${def.name} <kbd>${def.hotkey}</kbd></span>` +
      `<span class="cost">${costHtml(def.cost)}</span></span>`;
    btn.addEventListener('click', () => trainUnit(type));
    attachTooltip(btn, () => unitTooltip(type));
    train.appendChild(btn);
  });

  const boat = document.createElement('button');
  boat.className = 'pal-btn';
  boat.id = 'boat-btn';
  boat.innerHTML =
    `${ico('harbour', 26)}<span class="txt"><span class="ttl">Transport <kbd>H</kbd></span>` +
    '<span class="cost"><span class="c">troops by sea</span></span></span>';
  boat.addEventListener('click', () => toggleBoatMode());
  attachTooltip(boat, () =>
    '<h4>Troop transport</h4><div class="t-desc">Sails from your nearest Harbour to any shore you click ' +
    'and lands your attack ratio as an invasion. Defenceless at sea — sinking it drowns the men.</div>');
  train.appendChild(boat);
}

function structureTooltip(type: number, tile: number = state.hoverTile): string {
  const def = STRUCTURE_DEFS[type];
  // the one this click would actually level — on the tile or right beside it
  const existing = upgradeTargetAt(tile, type) || ownStructureAt(tile);
  let html = `<h4>${kindTag(def)}${def.name}</h4>`;

  /*
   * The price, first, above everything else.
   *
   * It is the line every player looks for and it used to sit at the very
   * bottom, under six lines of mechanics — so answering "can I afford this?"
   * meant reading past the part you do not need yet to reach the part that
   * decides whether you can click at all. The footnotes about escalating prices
   * stay at the bottom, where footnotes belong.
   */
  let priceNote = '';
  if (existing && existing.type === type) {
    if (existing.level >= MAX_STRUCTURE_LEVEL) {
      html += '<div class="t-line">Already at the highest level</div>';
    } else {
      html += `<div class="t-price">${costHtml(nextBuildingCost(type), 14)}</div>`;
      html += `<div class="t-line t-good">Clicking here <b>upgrades</b> it to level ${existing.level + 1}`
        + ' — the same price a new one would cost</div>';
    }
  } else {
    const next = nextBuildingCost(type);
    const owned = ownedOf(type);
    const cap = def.limit >= UNLIMITED ? '' : ` of ${def.limit}`;
    html += `<div class="t-price">${costHtml(next, 14)}</div>`;
    html += `<div class="t-line">You own ${owned}${cap}</div>`;
    if (owned > 0) {
      priceNote += '<div class="t-line">Build as many as you can pay for — every level you own, '
        + 'new hall or extra floor alike, makes the next one 60 % dearer</div>';
    }
    /*
     * Only when gold can actually stand in. A mana shortfall makes this
     * Infinity, because no amount of gold buys mana — saying "∞ gold would be
     * spent" would be worse than saying nothing.
     */
    const gold = goldShortfall(next);
    if (Number.isFinite(gold) && gold > (next[Res.Gold] || 0)) {
      priceNote += `<div class="t-line">${ico('gold', 11)} ${Math.round(gold)} gold would be spent — `
        + 'the treasury covers what you are short of</div>';
    } else if (!Number.isFinite(gold)) {
      priceNote += `<div class="t-line t-bad">${ico('mana', 11)} Gold cannot buy mana — `
        + 'this needs a Ley Shrine on a ley field</div>';
    }
  }

  html += `<div class="t-desc">${def.desc}</div>`;

  /*
   * The output, and immediately after it what this ground does to it.
   *
   * The terrain fit used to be four lines further down, which meant reading a
   * building's yield and its 20 % penalty as two separate facts and doing the
   * multiplication in your head. They are one fact. Putting the percentage
   * second — right against the figure it modifies — is the whole difference
   * between "+0.75 food/s" and "+0.75 food/s, of which you will see a fifth".
   */
  const fitHere = tile >= 0 && state.terrain ? terrainFactor(def, state.terrain[tile], state.race) : 1;
  const fitTag = fitHere >= 1
    ? '<b class="t-fit good">100 %</b>'
    : `<b class="t-fit bad">${Math.round(fitHere * 100)} %</b>`;
  // what it would produce *for you* — the specialist's bonus is part of the
  // price of the decision, so it belongs in the number you are shown
  const owned = myLevelsOf(def.key);
  const prod = def.income
    .map((_, r) => structureIncome(def, r, owned + 1) > 0
      ? `${ico(RES_KEY[r], 11)} +${structureIncome(def, r, owned + 1).toFixed(2)}/s per level ${fitTag}` : '')
    .filter(Boolean)
    .join('<br>');
  if (prod) html += `<div class="t-line">${prod}</div>`;
  if (def.popCap > 0) {
    // housing is a share of the whole ceiling, so it is worth the same to a
    // village of ten tiles and an empire of ten thousand
    const pct = Math.round(def.popCap * POP_HOUSING_PER_POINT * 100);
    html += `<div class="t-line t-good">${ico('pop', 11)} +${pct} % population ceiling per level ` +
      '<small>— no ceiling on how much housing can add; the rising price is the limit</small></div>';
  }
  if (type === StructureType.Village) {
    html += `<div class="t-line t-good">${ico('troops', 11)} +10 % recruitment speed per level ` +
      `(up to +${Math.round(TROOP_GROWTH_VILLAGE_CAP * 100)} % from all your villages). ` +
      'Your people are your army — this raises both the ceiling and the rate.</div>';
  }
  if (def.trains.length > 0) {
    html += '<div class="t-line">Raises your army limit — and raises what every other hall you own '
      + 'is worth, so the second is worth more than the first</div>';
  }
  /*
   * What a level buys a defensive building. Spelled out because it is the whole
   * reason to level one: a Watchtower earns nothing, houses nobody and trains
   * nothing, so if its levels are not legible they look like a waste of stone.
   */
  if (def.defenceBonus > 0) {
    const lvl = existing && existing.type === type ? existing.level : 1;
    const power = def.defenceBonus * (1 + (lvl - 1) * AURA_POWER_PER_LEVEL);
    const radius = def.defenceRadius * (1 + (lvl - 1) * AURA_RADIUS_PER_LEVEL);
    html += `<div class="t-line">${ico('watchtower', 11)} +${Math.round(power * 100)} % tile defence ` +
      `within ${radius.toFixed(1)} tiles <small>(level ${lvl})</small></div>`;
    html += `<div class="t-line t-good">Each level: +${Math.round(def.defenceBonus * AURA_POWER_PER_LEVEL * 100)} % ` +
      `defence and +${Math.round(AURA_RADIUS_PER_LEVEL * 100)} % radius</div>`;
  }
  if (def.zapDamage > 0) {
    const lvl = existing && existing.type === type ? existing.level : 1;
    const range = def.zapRange * (1 + (lvl - 1) * AURA_RADIUS_PER_LEVEL);
    html += `<div class="t-line t-good">${ico('troops', 11)} Shoots intruders for ` +
      `<b>${def.zapDamage * lvl}</b> a second within ${range.toFixed(1)} tiles ` +
      `<small>(+${def.zapDamage} damage and +${Math.round(AURA_RADIUS_PER_LEVEL * 100)} % range per level)</small></div>`;
  }
  if (def.coastal) {
    html += '<div class="t-line">Must be built beside water — sea, shallows or river</div>';
  } else {
    // a Village's favourite ground depends on who is building it
    const preferred = preferredTerrainFor(def, state.race);
    if (preferred) {
      const names = preferred.map(t => TERRAIN_NAME[t]).join(', ');
      html += `<div class="t-line">Best on <b>${names}</b> — elsewhere ${Math.round(WRONG_TERRAIN_FACTOR * 100)} % output</div>`;
      if (def.preferredByRace) {
        html += '<div class="t-line"><small>Your people\'s own idea of home — every race wants different ground</small></div>';
      }
    } else {
      html += '<div class="t-line">Works everywhere at full output</div>';
    }
  }

  if (tile >= 0 && state.terrain) {
    const terrain = state.terrain[tile];
    const f = terrainFactor(def, terrain, state.race);
    const cls = f >= 1 ? 't-good' : 't-bad';
    html += `<div class="t-line ${cls}">Here (${TERRAIN_NAME[terrain]}): ${Math.round(f * 100)} % output</div>`;
  }

  return html + priceNote;
}

/**
 * How many of this unit you have, live and queued.
 *
 * There is no per-type cap any more — the army as a whole is the only ceiling —
 * so this counts and does not judge. The count is still wanted, because the
 * *price* of the next one is driven by how many of that kind you already have.
 */
export function unitCapInfo(type: number): { count: number; cap: number } {
  let count = 0;
  for (const u of state.units.values()) {
    if (u.owner === state.you && u.type === type) count++;
  }
  for (const s of state.structures.values()) {
    if (s.owner !== state.you) continue;
    count += (s.jobs || []).filter(j => j.type === type).length;
  }
  return { count, cap: armyInfo().cap };
}

/** What the next one of this unit will actually cost, at today's army size. */
function nextUnitCost(type: number): number[] {
  return unitCost(UNIT_DEFS[type], unitCapInfo(type).count);
}

/** And what it costs in people, which is not one of the five and is not optional. */
function nextUnitPop(type: number): number {
  return unitPopCost(UNIT_DEFS[type], unitCapInfo(type).count);
}

/** Your whole army, and what your halls can support. */
function armyInfo(): { size: number; cap: number } {
  let size = 0;
  for (const u of state.units.values()) {
    if (u.owner !== state.you) continue;
    if (isCivilianUnit(u.type)) continue;
    size++;
  }
  for (const s of state.structures.values()) {
    if (s.owner !== state.you) continue;
    size += (s.jobs || []).length;
  }
  return { size, cap: state.self.armyCap };
}

/**
 * The wheel's version of a building panel: **four facts and no prose.**
 *
 * The wheel is a thing you open, glance at and click, usually with the cursor
 * already moving — the full panel's six paragraphs of mechanics belong on the
 * left, where you go to *read* about a building before you commit to it. Here
 * you want the price, whether this ground suits it, and the one line saying why
 * the slice is greyed out. Everything else is noise between you and the click.
 */
function radialStructureTip(type: number, tile: number, why: string): string {
  const def = STRUCTURE_DEFS[type];
  const up = upgradeTargetAt(tile, type);
  // one price either way now, so the wheel never has to explain a difference
  const cost = nextBuildingCost(type);
  const fit = tile >= 0 && state.terrain ? terrainFactor(def, state.terrain[tile], state.race) : 1;

  let html = `<h4>${kindTag(def)}${def.name}${up ? ` <small>→ level ${up.level + 1}</small>` : ''}</h4>`;
  html += `<div class="t-price">${costHtml(cost, 14)}</div>`;

  // where it pays, and what it pays here — the two numbers a click turns on
  const best = preferredTerrainFor(def, state.race);
  if (def.coastal) {
    html += '<div class="t-big">Must stand beside water</div>';
  } else if (tile >= 0 && state.terrain) {
    const cls = fit >= 1 ? 'good' : 'bad';
    html += `<div class="t-big ${cls}">${Math.round(fit * 100)} % output here`
      + `<small> · ${TERRAIN_NAME[state.terrain[tile]]}</small></div>`;
    if (fit < 1 && best) {
      html += `<div class="t-line">Best on <b>${best.map(t => TERRAIN_NAME[t]).join(', ')}</b></div>`;
    }
  }
  if (why) html += `<div class="t-line t-bad">${why}</div>`;
  return html;
}

/**
 * What this soldier does when it meets one of theirs.
 *
 * "0 dmg · melee" reads as a bug rather than as a design, so a soldier who
 * draws no blood says so in words — it is the single most important thing to
 * know about a Shieldbearer before you spend seventy stone on one. Everyone
 * else gets their siege strength shown only when it differs from their damage,
 * which is exactly the units bought with mana.
 */
function weaponLine(def: UnitDef): string {
  const siege = siegePower(def);
  if (def.damage <= 0) {
    return siege > 0
      ? `<b>draws no blood</b> · ${siege} vs walls`
      : '<b>strikes no blow</b>';
  }
  const reach = def.range > 2 ? `range ${def.range}` : 'melee';
  const wall = siege !== def.damage ? ` · <b>${siege} vs walls</b>` : '';
  return `${def.damage} dmg · ${reach}${wall}`;
}

function unitTooltip(type: number): string {
  const def = UNIT_DEFS[type];
  const { count, cap } = unitCapInfo(type);
  const army = armyInfo();
  let html = `<h4>${def.name}</h4><div class="t-desc">${def.desc}</div>`;
  html += `<div class="t-line">${def.hp} HP · ${weaponLine(def)}</div>`;
  if (count > 0) html += `<div class="t-line">You have ${count} of these</div>`;
  html += `<div class="t-line ${army.size >= army.cap ? 't-bad' : ''}">Army ${army.size} of ${army.cap} — ` +
    'the only limit. Any mix you like; every military hall raises it, and each one raises what all ' +
    'the others are worth</div>';
  if (count > 0) {
    html += `<div class="t-line">This one costs ${costHtml(nextUnitCost(type))} — ` +
      `each of the ${count} already in the field makes the next ` +
      `+${Math.round((UNIT_COST_STEP - 1) * 100)} % dearer, compounding</div>`;
  }
  if (def.defencePower > 0 || def.assaultPower > 0) {
    html += `<div class="t-line">Ground control: <b>${def.defencePower.toFixed(1)}</b> holding, ` +
      `<b>${def.assaultPower.toFixed(1)}</b> assaulting</div>`;
  }
  if (def.heals) html += '<div class="t-line t-good">Mends the most wounded ally in range instead of attacking</div>';
  if (def.rebirth) html += '<div class="t-line t-good">Rises again once at half health when killed</div>';
  return html;
}

function ownStructureAt(tile: number) {
  if (tile < 0) return null;
  for (const s of state.structures.values()) {
    if (s.tile === tile && s.owner === state.you) return s;
  }
  return null;
}

/**
 * The building a build order aimed at this tile would actually level up.
 *
 * Mirrors the server's `upgradeTargetNear` — on or beside the tile, yours, same
 * type — so the price the palette shows is the price you are about to pay.
 */
function upgradeTargetAt(tile: number, type: number): ClientStruct | null {
  if (tile < 0) return null;
  const cx = tile % MAP_W, cy = (tile / MAP_W) | 0;
  let best: ClientStruct | null = null;
  let bestD = Infinity;
  for (const s of state.structures.values()) {
    if (s.owner !== state.you || s.type !== type) continue;
    const dx = (s.tile % MAP_W) - cx, dy = ((s.tile / MAP_W) | 0) - cy;
    if (Math.abs(dx) > UPGRADE_SNAP_RADIUS || Math.abs(dy) > UPGRADE_SNAP_RADIUS) continue;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = s; }
  }
  return best;
}

function structureAt(tile: number) {
  if (tile < 0) return null;
  for (const s of state.structures.values()) if (s.tile === tile) return s;
  return null;
}

/* ------------------------------------------------------ kingdom card */

const RELATION_LABEL: Record<string, string> = {
  self: 'you', team: 'your team', enemy: 'not your friend',
};

/**
 * Everything you are allowed to know about a kingdom. Strength is public — any
 * scout can count banners — but the storehouses are only open to allies.
 */
export function kingdomCard(id: number): string {
  const p = state.players.get(id);
  if (!p) return '';
  const rel = relationColorTag(id);
  const kind = p.kind === PlayerKind.Camp ? 'monster camp'
    : p.kind === PlayerKind.AiKingdom ? 'AI kingdom' : RELATION_LABEL[rel];

  let html = `<h4 style="color:${hexString(p.color)}">${escapeHtml(p.name)}${raceTag(p)}</h4>`;
  html += `<div class="t-desc">${kind}${p.team >= 0 ? ` · ${TEAM_DEFS[p.team % TEAM_DEFS.length].name} team` : ''}` +
    `${p.eliminated ? ' · <b>wiped out</b>' : ''}` +
    `${!p.connected && p.kind === PlayerKind.Human ? ' · offline' : ''}</div>`;
  html += `<div class="t-line">${ico('land', 11)} ${shortNumber(p.tiles)} tiles</div>`;
  html += `<div class="t-line"><b>${ico('troops', 11)} ${shortNumber(p.troops)} of ${shortNumber(p.troopCap)}</b>` +
    ` troops${p.troopRate > 0.05 ? ` · +${p.troopRate.toFixed(1)}/s` : ''}</div>`;
  if (p.protectedNow) html += '<div class="t-line t-good">Under spawn protection</div>';

  const books = state.allyBooks.get(id);
  if (id === state.you) {
    const mine: string[] = [];
    for (let r = 0; r < RES_COUNT; r++) mine.push(`${ico(RES_KEY[r], 11)}${shortNumber(state.self.res[r])}`);
    html += `<div class="t-line">${mine.join(' ')}</div>`;
  } else if (books) {
    html += '<div class="t-line">' +
      books.res.map((v, r) => `${ico(RES_KEY[r], 11)}${shortNumber(v)}`).join(' ') + '</div>';
    html += `<div class="t-line ${books.food < 0.01 ? 't-bad' : ''}">Food income ${books.food.toFixed(1)}/s` +
      `${books.food < 0.01 ? ' — they are starving, send food' : ''}</div>`;
  } else if (p.kind !== PlayerKind.Camp) {
    html += '<div class="t-line">Their storehouses are closed to you — <b>ally them</b> to see what they hold.</div>';
  }
  return html;
}

/**
 * One line about whose land this is, for the strip across the top.
 *
 * Deliberately short. The full card is for a *thing* — a soldier, a hall, a
 * stretch of wall — because those are what you point at deliberately. Land is
 * what the cursor is over the rest of the time, and all anybody wants from it
 * is: whose, which people, how big, and are we fighting.
 */
function realmStrip(id: number): string {
  const p = state.players.get(id);
  if (!p) return '';
  const rel = relationColorTag(id);
  const kind = p.kind === PlayerKind.Camp ? 'monster camp'
    : p.kind === PlayerKind.AiKingdom ? 'AI kingdom' : RELATION_LABEL[rel];
  const war = atWarWith(id) ? '<b class="war">at war</b>' : '';
  const protect = p.protectedNow ? '<b class="safe">under spawn protection</b>' : '';
  return [
    `<span class="chip" style="background:${hexString(p.color)}"></span>`,
    `<b>${escapeHtml(p.name)}</b>${raceTag(p)}`,
    kind,
    `${shortNumber(p.tiles)} tiles`,
    `${ico('troops', 11)} ${shortNumber(p.troops)} / ${shortNumber(p.troopCap)}`,
    war,
    protect,
  ].filter(Boolean).join('<i class="sep"></i>');
}

/**
 * What is under the cursor: a soldier first, then a building, then the land.
 *
 * A *thing* gets the full card beside the cursor, because you pointed at it on
 * purpose and the answer belongs next to it. Bare **land** is different: it is
 * what the cursor is over by default, so it goes to the strip at the top and
 * never to a panel in the way. Your own land says nothing at all.
 */
function hoverSubject(): { html: string; realm: boolean } {
  const tile = state.hoverTile;
  if (tile < 0) return { html: '', realm: false };
  const cx = (tile % MAP_W) + 0.5, cy = ((tile / MAP_W) | 0) + 0.5;

  let best: ClientUnit | null = null;
  let bestD = 1.6 * 1.6;
  for (const u of state.units.values()) {
    const dx = u.tx - cx, dy = u.ty - cy;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD) { bestD = d2; best = u; }
  }
  if (best) return { html: unitCard(best), realm: false };

  for (const s of state.structures.values()) {
    if (s.tile === tile) return { html: structureCard(s), realm: false };
  }
  const wall = state.walls.get(tile);
  if (wall) return { html: wallCard(wall), realm: false };

  const owner = state.owner[tile];
  // your own province: you know whose it is, and you know how many men you have
  if (owner === 0 || owner === state.you) return { html: '', realm: true };
  return { html: realmStrip(owner), realm: true };
}

/** A piece of somebody's border, and how much of it is left. */
function wallCard(w: ClientWall): string {
  const gate = w.kind === WallKind.Gate;
  const owner = state.players.get(w.owner);
  const intact = Math.round((w.hp / 255) * 100);
  let html = `<h4>${ico(gate ? 'gate' : 'wall', 14)} ${gate ? 'Gate' : 'Wall'}</h4>`;
  html += `<div class="t-desc">${gate
    ? 'A way through. Only its owner and their allies walk through a gate — and no land is taken through one until it falls.'
    : 'No land is taken through it while it stands, and nothing on foot crosses it.'}</div>`;
  if (owner) {
    html += `<div class="t-line"><span class="chip" style="background:${hexString(owner.color)}"></span>` +
      `${escapeHtml(owner.name)} · ${RELATION_LABEL[relationColorTag(w.owner)] || ''}</div>`;
  }
  html += `<div class="t-line ${intact < 40 ? 't-bad' : ''}">${intact} % intact</div>`;
  return html;
}

/** A soldier on the field: what it is, whose it is, and how hurt it is. */
function unitCard(u: ClientUnit): string {
  const def = UNIT_DEFS[u.type];
  const owner = state.players.get(u.owner);
  const hp = Math.round((u.hp / 255) * 100);
  let html = `<h4>${ico(def.key, 14)} ${def.name}</h4>`;
  html += `<div class="t-desc">${def.desc}</div>`;
  if (owner) {
    html += `<div class="t-line"><span class="chip" style="background:${hexString(owner.color)}"></span>` +
      `${escapeHtml(owner.name)} · ${RELATION_LABEL[relationColorTag(u.owner)] || ''}</div>`;
  }
  html += `<div class="t-line ${hp < 40 ? 't-bad' : ''}">${hp} % health · ${weaponLine(def)}` +
    `${def.flying ? ' · flies' : ''}</div>`;
  if (def.heals) html += '<div class="t-line t-good">Mends the most wounded ally in range</div>';
  if (def.rebirth) html += '<div class="t-line t-good">Rises again once when killed</div>';
  return html;
}

/** A building on the map: what it is, whose, how hurt, and what is in the oven. */
function structureCard(s: ClientStruct): string {
  const def = STRUCTURE_DEFS[s.type];
  const owner = state.players.get(s.owner);
  const hp = Math.round((s.hp / 255) * 100);
  let html = `<h4>${ico(def.key, 14)} ${kindTag(def)}${def.name}` +
    `${s.level > 1 ? ` <small>level ${s.level}</small>` : ''}</h4>`;
  html += `<div class="t-desc">${def.desc}</div>`;
  if (owner) {
    html += `<div class="t-line"><span class="chip" style="background:${hexString(owner.color)}"></span>` +
      `${escapeHtml(owner.name)} · ${RELATION_LABEL[relationColorTag(s.owner)] || ''}</div>`;
  }
  // your own buildings can be priced exactly; somebody else's holdings are not
  // ours to count, so theirs are shown at the bare rate
  const held = s.owner === state.you ? myLevelsOf(def.key) : 0;
  const prod = def.income
    .map((_, r) => structureIncome(def, r, held) > 0
      ? `${ico(RES_KEY[r], 11)} +${(structureIncome(def, r, held) * s.level).toFixed(2)}/s` : '')
    .filter(Boolean).join(' ');
  if (prod) html += `<div class="t-line">${prod}</div>`;
  html += `<div class="t-line ${hp < 40 ? 't-bad' : ''}">${hp} % intact</div>`;
  for (const job of s.jobs || []) {
    const udef = UNIT_DEFS[job.type];
    if (!udef) continue;
    html += `<div class="t-line">${ico(udef.key, 11)} training ${udef.name} — ${Math.round(job.progress * 100)} %</div>`;
  }
  return html;
}

/**
 * The two hover readouts: a card beside the cursor for a thing, a strip across
 * the top for a realm. Exactly one of them is ever visible.
 */
function renderHoverCard() {
  const card = $('hover-card');
  const strip = $('realm-strip');
  if (!state.overWorld || state.phase !== Phase.Live || radialOpen() || aidPanelOpen()) {
    card.classList.add('hidden');
    strip.classList.add('hidden');
    return;
  }

  const { html, realm } = hoverSubject();
  if (realm) {
    card.classList.add('hidden');
    // empty wilderness and your own ground have nothing worth saying
    if (!html) { strip.classList.add('hidden'); return; }
    strip.innerHTML = html;
    strip.classList.remove('hidden');
    return;
  }

  strip.classList.add('hidden');
  if (!html) { card.classList.add('hidden'); return; }
  card.innerHTML = html;
  card.classList.remove('hidden');
  const w = card.offsetWidth || 230;
  const h = card.offsetHeight || 140;
  const left = state.cursorX + 18 + w > window.innerWidth ? state.cursorX - w - 18 : state.cursorX + 18;
  card.style.left = `${Math.max(6, left)}px`;
  card.style.top = `${Math.max(6, Math.min(window.innerHeight - h - 6, state.cursorY + 14))}px`;
}

/**
 * Openfront's boat flow: pick the unit, then click where it should go. The
 * server builds it in whichever barracks can get it there soonest.
 */
export function trainUnit(type: number) {
  const def = UNIT_DEFS[type];
  if (!def.trainable) return;
  let has = false;
  for (const s of state.structures.values()) {
    if (s.owner === state.you && STRUCTURE_DEFS[s.type].trains.includes(type)) { has = true; break; }
  }
  if (!has) {
    notice(`No building can train ${def.name} — build a ${STRUCTURE_DEFS[def.from[0]].name} first.`, 'bad');
    return;
  }
  const { count, cap } = unitCapInfo(type);
  if (count >= cap) {
    notice(`${def.name} limit reached (${count}/${cap}) — build or level up another ${STRUCTURE_DEFS[def.from[0]].name}.`, 'bad');
    return;
  }
  const army = armyInfo();
  if (army.size >= army.cap) {
    notice(`Your army is full (${army.size}/${army.cap}) — raise another military hall, or level one up.`, 'bad');
    return;
  }
  state.trainType = state.trainType === type ? -1 : type;
  state.buildType = -1;
  state.boatMode = false;
  state.wallMode = 0;
  refreshPalettes();
}

/**
 * The fighting units in hand right now.
 *
 * Transports and caravans are excluded: they never attack anything, so an order
 * about *what to attack* is meaningless to them, and a bar that appears when you
 * click a boat is a bar that teaches you to ignore it.
 */
function selectedFighters(): number[] {
  const out: number[] = [];
  for (const id of state.selected) {
    const u = state.units.get(id);
    if (!u || isCivilianUnit(u.type)) continue;
    out.push(id);
  }
  return out;
}

/**
 * Everything of yours in hand, civilians included.
 *
 * `selectedFighters` is for *orders about fighting*, which mean nothing to a
 * wagon. Scrapping is different: a caravan that has gone wrong — bricked in,
 * bound somewhere useless, or simply in the way — has to be something you can
 * get rid of, and until now selecting one gave you no bar at all and therefore
 * no way to do it short of a keyboard shortcut nobody was told about.
 */
function selectedOwn(): number[] {
  const out: number[] = [];
  for (const id of state.selected) {
    const u = state.units.get(id);
    if (u && u.owner === state.you) out.push(id);
  }
  return out;
}

/**
 * **Every war you have open, and a way out of each one.**
 *
 * An attack is a share of your people set aside to push one border, and you can
 * have several running at once — which used to be completely invisible. You
 * could see your reserve draining and had no way to find out which of three
 * commitments was doing it, still less to change your mind about one of them.
 *
 * Each row is one attack: how many, and against whom. The X calls it off and
 * the men are back in the reserve the same instant — the ground already taken
 * stays taken, so a cancelled attack costs nothing but the ground you did not
 * go on to take. That makes an over-committed attack a mistake you can undo
 * instead of a mistake you have to watch.
 */
function renderAttackList() {
  const box = $('attack-list');
  if (state.phase !== Phase.Live || state.attacks.length === 0) {
    box.classList.add('hidden');
    box.replaceChildren();
    return;
  }
  box.classList.remove('hidden');
  box.replaceChildren();

  for (const a of state.attacks) {
    const foe = a.target === 0 ? null : state.players.get(a.target);
    const row = document.createElement('div');
    row.className = 'atk-row';

    const who = document.createElement('span');
    who.className = 'atk-who';
    if (foe) {
      who.innerHTML = `<span class="chip" style="background:${hexString(foe.color)}"></span>` +
        `${escapeHtml(foe.name)}${raceTag(foe)}`;
    } else {
      // the one you opened on nobody's land — it has no banner to show
      who.innerHTML = '<span class="chip wild"></span>open land';
    }

    const men = document.createElement('span');
    men.className = 'atk-men';
    men.innerHTML = `${ico('troops', 11)} ${shortNumber(a.troops)}`;

    const off = document.createElement('button');
    off.className = 'atk-off';
    off.textContent = '✕';
    off.title = foe
      ? `Call off the attack on ${foe.name} — every man comes home at once`
      : 'Call off the expansion — every man comes home at once';
    off.addEventListener('click', (e) => {
      e.stopPropagation();
      net.send({ t: 'cancel', target: a.target });
      sfx.play('click');
    });

    row.append(who, men, off);
    box.appendChild(row);
  }
}

/** The orders bar under the troop bar: what is selected, and what it is told. */
function renderSquadRow() {
  const row = $('squad-row');
  const mine = selectedOwn();
  const fighters = selectedFighters();
  row.classList.toggle('hidden', mine.length === 0);
  if (mine.length === 0) return;

  $('squad-count').textContent = `${mine.length} selected`;

  // "spare buildings" is an order about fighting, so it is offered only when
  // something in hand can actually fight
  const spare = $('squad-spare');
  spare.classList.toggle('hidden', fighters.length === 0);
  if (fighters.length > 0) {
    const sparing = fighters
      .filter(id => (state.units.get(id)!.flags & UNIT_FLAG_SPARE_BUILDINGS) !== 0).length;
    const all = sparing === fighters.length;
    spare.classList.toggle('on', all);
    spare.innerHTML = `${ico(all ? 'village' : 'knight', 13)}` +
      `<span>${all ? 'Sparing buildings' : 'Razing buildings'}</span>`;
  }

  // a trader is scrapped, not disbanded — there are no soldiers in it to send home
  const civilians = mine.length - fighters.length;
  $('squad-disband').textContent = fighters.length === 0 && civilians > 0 ? 'Scrap' : 'Disband';
}

/**
 * "Leave the buildings standing" for the current selection.
 *
 * A captured province is worth far more than a razed one, and until now your own
 * army would happily flatten the market you were about to inherit. Watchtowers
 * are always fair game — they shoot back.
 */
export function toggleSpareBuildings() {
  const ids = selectedFighters();
  if (ids.length === 0) {
    notice('Select some units first — this is an order, and somebody has to hear it.', 'bad');
    return;
  }
  // whatever most of the selection is doing now, do the opposite for all of them
  const sparing = ids.filter(id => (state.units.get(id)!.flags & UNIT_FLAG_SPARE_BUILDINGS) !== 0).length;
  const spare = sparing * 2 < ids.length;
  // set it locally too, so the HUD answers the keypress before the server does
  for (const id of ids) {
    const u = state.units.get(id)!;
    u.flags = spare ? u.flags | UNIT_FLAG_SPARE_BUILDINGS : u.flags & ~UNIT_FLAG_SPARE_BUILDINGS;
  }
  net.send({ t: 'spare', ids, spare });
  sfx.play('click');
}

export function toggleBoatMode() {
  let harbour = false;
  for (const s of state.structures.values()) {
    if (s.owner === state.you && s.type === StructureType.Harbour) { harbour = true; break; }
  }
  if (!harbour) {
    notice('Build a Harbour on a coastal tile first — transports sail from there.', 'bad');
    return;
  }
  state.boatMode = !state.boatMode;
  state.trainType = -1;
  state.buildType = -1;
  state.wallMode = 0;
  refreshPalettes();
}

/**
 * **Puts the wall tool in hand — a wall is drawn, not placed.**
 *
 * A rampart is the one thing in the game whose *shape* is the decision: where
 * the line runs, how it meets your coast, which gap is left for the gate. So
 * choosing Wall does not lay a stone anywhere — it arms the drawing tool, and
 * the next drag across your own ground is the wall. Let go and the whole line
 * is sent as one order, and the tool is put down again (`input.ts`), because
 * one gesture that leaves nothing in hand is the only kind of tool a touch
 * screen can offer safely: there is no cursor on a phone to show what is armed.
 *
 * A tap with no drag in it is a line one tile long, which is how a single
 * segment or a lone gate is laid.
 */
export function armWallTool(kind: number) {
  state.wallMode = kind;
  state.wallDrag.length = 0;
  state.buildType = -1;
  state.trainType = -1;
  state.boatMode = false;
  state.selected.clear();
  refreshPalettes();
  sfx.play('click');
}

/**
 * **Everything a click could currently mean other than "look at that".**
 *
 * One list, asked by the two places that need it: the key that cancels, and the
 * button a phone cancels with. Kept together because a tool added to one and
 * forgotten in the other is a tool that cannot be put down.
 */
export function toolInHand(): boolean {
  return state.wallMode !== 0 || state.buildType >= 0 || state.trainType >= 0
    || state.boatMode || state.powerMode || pingMode >= 0;
}

/** Puts down whatever is in hand, whatever it is. */
export function dropTool() {
  state.buildType = -1;
  state.trainType = -1;
  state.boatMode = false;
  state.wallMode = 0;
  state.wallDrag.length = 0;
  state.powerMode = false;
  setPingMode(-1);
  refreshPalettes();
}

/** The palette's version: pressing the armed tool again puts it down. */
export function toggleWallMode(kind: number) {
  if (state.wallMode === kind) {
    state.wallMode = 0;
    state.wallDrag.length = 0;
    refreshPalettes();
    sfx.play('click');
    return;
  }
  armWallTool(kind);
}

function refreshPalettes() {
  for (const el of document.querySelectorAll('#build-palette .pal-btn')) {
    // the two wall tools are not structures and have no `type`; price them off
    // the running total the server sends, not off the table
    const rawWall = (el as HTMLElement).dataset.wall;
    if (rawWall !== undefined) {
      const kind = Number(rawWall);
      const cost = nextWallCost(kind === WallKind.Gate);
      el.classList.toggle('active', state.wallMode === kind);
      el.classList.toggle('cant', !affordable(cost));
      const wc = el.querySelector('.cost');
      if (wc) wc.innerHTML = costHtml(cost);
      continue;
    }
    const type = Number((el as HTMLElement).dataset.type);
    const def = STRUCTURE_DEFS[type];
    // hovering on or beside one of your own prices the upgrade it would become,
    // otherwise the next new one
    const hovered = upgradeTargetAt(state.hoverTile, type);
    const cost = nextBuildingCost(type);
    el.classList.toggle('active', state.buildType === type);
    el.classList.toggle('upgrade', !!hovered);
    el.classList.toggle('cant', !affordable(cost));
    const costEl = el.querySelector('.cost');
    if (costEl) costEl.innerHTML = costHtml(cost);
  }
  for (const el of document.querySelectorAll('#train-palette .pal-btn')) {
    const raw = (el as HTMLElement).dataset.unit;
    if (raw === undefined) {   // the transport button
      el.classList.toggle('active', state.boatMode);
      continue;
    }
    const type = Number(raw);
    const def = UNIT_DEFS[type];
    const { count, cap } = unitCapInfo(type);
    const army = armyInfo();
    const price = nextUnitCost(type);
    el.classList.toggle('cant', !affordable(price) || count >= cap || army.size >= army.cap);
    el.classList.toggle('active', state.trainType === type);
    const ttl = el.querySelector('.ttl');
    if (ttl) ttl.innerHTML = `${def.name} <kbd>${def.hotkey}</kbd> <small>${count}/${cap}</small>`;
    const cost = el.querySelector('.cost');
    if (cost) cost.innerHTML = costHtml(price);
  }
  renderTraining();
}

/** Live view of what is in the ovens and how far along it is. */
function renderTraining() {
  const el = $('training-list');
  const rows: string[] = [];
  for (const s of state.structures.values()) {
    if (s.owner !== state.you || !s.jobs || s.jobs.length === 0) continue;
    const def = STRUCTURE_DEFS[s.type];
    s.jobs.forEach((job, i) => {
      const udef = UNIT_DEFS[job.type];
      if (!udef) return;
      rows.push(
        `<div class="train-row">${ico(udef.key, 13)}` +
        `<span class="bar"><i style="width:${Math.round((i === 0 ? job.progress : 0) * 100)}%"></i></span>` +
        `<span class="where">${def.name.split(' ')[0]}</span></div>`,
      );
    });
  }
  el.innerHTML = rows.join('');
}

/* ------------------------------------------------------- radial menu */

/**
 * Openfront's right-click wheel. With nothing selected, right-clicking a tile
 * asks "what can I do *here*" and answers it in one gesture: the inner ring
 * recruits (the unit marches to that tile), the outer ring builds. Anything the
 * tile cannot take is drawn dimmed with the reason on its tooltip, so the menu
 * teaches the rules instead of silently refusing.
 */
/**
 * `why` and `cost` are **functions**, not values.
 *
 * The wheel used to freeze the moment it opened: a Barracks you were forty wood
 * short of stayed greyed out while the wood arrived, and you had to close and
 * re-open the menu to notice. Asking each slice for its own reason every HUD
 * tick means the wheel is a live answer to "what can I do here" rather than a
 * snapshot of what you could do a moment ago.
 */
interface RadialItem {
  icon: string;
  name: string;
  cost: () => number[] | null;
  why: () => string;
  tip: () => string;
  run: () => void;
  /**
   * Whether the wheel stays open after this slice is used.
   *
   * Set on recruits only. Ordering soldiers is a repeated gesture — three
   * knights, five archers — and closing after each one meant re-opening the
   * wheel per soldier. Everything else here happens once.
   */
  repeatable?: boolean;
}

let radialTile = -1;
/** the live wheel, so it can be re-priced without being rebuilt under the cursor */
let radialLive: { item: RadialItem; btn: HTMLElement; cost: HTMLElement | null }[] = [];

export const radialOpen = () => radialTile >= 0;

export function closeRadial() {
  if (radialTile < 0) return;
  radialTile = -1;
  radialLive = [];
  $('radial').classList.add('hidden');
}

/**
 * Client-side mirror of `Game.isCoastal` — water of any kind, on any of eight
 * sides. Rivers count and so do diagonals.
 *
 * It has to agree with the server exactly or the UI lies: this is what greys
 * out the Harbour and writes "must touch water", and a stricter mirror would
 * refuse to draw a button for a build the server would have happily accepted.
 */
function isCoastalTile(tile: number): boolean {
  if (!state.terrain || tile < 0) return false;
  if (state.terrain[tile] < Terrain.Plains) return false;
  const x = tile % MAP_W, y = (tile / MAP_W) | 0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
      if (state.terrain[ny * MAP_W + nx] < Terrain.Plains) return true;
    }
  }
  return false;
}

function ownedOf(type: number): number {
  let n = 0;
  for (const s of state.structures.values()) if (s.owner === state.you && s.type === type) n++;
  return n;
}

function buildRadialItems(tile: number): RadialItem[] {
  const items: RadialItem[] = [];
  const mine = state.owner[tile] === state.you;
  const here = structureAt(tile);
  const terrain = state.terrain ? state.terrain[tile] : -1;
  let total = 0;
  for (const s of state.structures.values()) if (s.owner === state.you) total++;

  /*
   * **Water gets one thing: a warship.**
   *
   * Nothing else in the game can be done to open sea, so the wheel over water
   * is this and nothing else — which is also why it goes first and why the
   * whole rest of this function, all of which is about dry land, is skipped
   * below when the tile is wet.
   */
  if (terrain >= 0 && terrain < Terrain.Plains) {
    const def = UNIT_DEFS[UnitType.Warship];
    const berths = [...state.structures.values()]
      .filter(s => s.owner === state.you && s.type === StructureType.Harbour && s.built >= 1)
      .reduce((n, s) => n + s.level, 0);
    let afloat = 0;
    for (const u of state.units.values()) {
      if (u.owner === state.you && u.type === UnitType.Warship) afloat++;
    }
    const cost = () => unitCost(def, afloat);
    const why = () => {
      if (state.defeated) return 'your kingdom has fallen';
      if (berths === 0) return 'you need a finished Harbour';
      if (afloat >= berths) return `every berth is taken (${afloat}/${berths}) — level a Harbour up`;
      const army = armyInfo();
      if (army.size >= army.cap) return `your army is full (${army.size}/${army.cap})`;
      if (!affordable(cost())) return 'you cannot pay for it';
      return '';
    };
    items.push({
      icon: 'tradeship',
      name: def.name,
      cost,
      why,
      tip: () => `<h4>${def.name}</h4>`
        // the price first, as everything else in this interface does — a hull
        // is bought in timber and coin, and that is the first thing to know
        + `<div class="t-line">Costs ${costHtml(cost(), 11)}</div>`
        + `<div class="t-desc">${def.desc}</div>`
        + `<div class="t-line">Berths in use: <b>${afloat} / ${berths}</b> — one per Harbour level</div>`
        + (why() ? `<div class="t-line t-bad">${why()}</div>` : ''),
      run: () => { net.send({ t: 'warship', tile }); sfx.play('train'); },
    });
    return items;
  }

  /*
   * **The wheel hands you the trowel; it does not lay the stone.**
   *
   * This slice used to drop a single segment on the tile the wheel was opened
   * on, which quietly made the wheel the *worst* way to build a wall: a border
   * is twenty tiles long, so it was twenty menus. Worse on a phone, where the
   * wheel is the only way to reach the tool at all — the build palette is not
   * on a touch screen — so drawing a wall was simply not available there.
   *
   * Now it arms the drawing tool and closes, and the next drag is the wall. A
   * tap with no drag still lays exactly one segment, so nothing that could be
   * done before has been taken away.
   */
  for (const kind of [WallKind.Rampart, WallKind.Gate]) {
    const gate = kind === WallKind.Gate;
    const cost = () => nextWallCost(gate);
    const why = () => {
      /*
       * **Anything standing here blocks it — a Watchtower included.**
       *
       * The tower used to be allowed to share the ground with a rampart and is
       * not any more (`canWall` on the server, and `addStructure` from the
       * other direction). This exemption outlived the rule it belonged to, so
       * the wheel went on offering Wall on a tower's tile and the server went
       * on refusing it: an enabled slice that does nothing is worse than a
       * dimmed one that says why.
       */
      if (!mine) return 'not your land';
      if (state.walls.get(tile)?.kind === kind) return 'already what you asked for';
      if (here) return `a ${STRUCTURE_DEFS[here.type].name} stands here`;
      if (!state.walls.has(tile) && terrain < Terrain.Plains) return 'walls need dry ground';
      if (!affordable(cost())) return 'you cannot pay for it';
      return '';
    };
    items.push({
      icon: gate ? 'gate' : 'wall',
      name: gate ? 'Gate' : 'Wall',
      cost,
      why,
      tip: () => wallTooltip(kind)
        + '<div class="t-line t-good">Picks up the tool — then <b>drag</b> across your own '
        + 'ground to draw the line, and let go to build it. One tap is one segment.</div>'
        + (why() ? `<div class="t-line t-bad">${why()}</div>` : ''),
      run: () => armWallTool(kind),
    });
  }

  STRUCTURE_DEFS.forEach((def, type) => {
    if (type === StructureType.Keep || def.buildable === false) return;
    if (def.race !== undefined && def.race !== state.race) return;
    /*
     * And nothing this edition does not contain.
     *
     * The wheel builds its own list rather than reading the palette's, so
     * filtering `buildPalettes` left the Ley Shrine, the Thieves' Den, the
     * Warehouse and the Watchtower still on offer here — buttons the server
     * refuses, which is worse than not offering them at all.
     */
    if (!structureInEdition(type as StructureType, editionInPlay())) return;
    // one of yours on or beside the tile turns this slice into an upgrade —
    // the same rule the server applies, so the wheel never promises otherwise
    const up = upgradeTargetAt(tile, type);
    const upgrade = !!up;
    const cost = () => nextBuildingCost(type);

    // ground rules only bind a *new* building; levelling an existing one is
    // legal wherever it already stands, and only its price can refuse it
    const why = () => {
      const owned = ownedOf(type);
      if (!mine && !upgrade) return 'not your land';
      if (here && !upgrade) return `a ${STRUCTURE_DEFS[here.type].name} already stands here`;
      if (!upgrade && def.required && !def.required.includes(terrain as Terrain)) {
        return `only on ${def.required.map(t => TERRAIN_NAME[t]).join(' / ')}`;
      }
      if (!upgrade && def.coastal && !isCoastalTile(tile)) return 'must touch water';
      if (!upgrade && owned >= def.limit) return `you already own ${owned} of ${def.limit}`;
      if (!affordable(cost())) return 'you cannot pay for it';
      return '';
    };

    items.push({
      icon: def.key,
      name: upgrade ? `${def.name} ${up!.level + 1}` : def.name,
      cost,
      why,
      tip: () => radialStructureTip(type, tile, why()),
      run: () => { net.send({ t: 'build', s: type, tile }); sfx.play('build'); },
    });
  });
  return items;
}

function trainRadialItems(tile: number): RadialItem[] {
  const items: RadialItem[] = [];
  const fx = (tile % MAP_W) + 0.5;
  const fy = ((tile / MAP_W) | 0) + 0.5;

  UNIT_DEFS.forEach((def, type) => {
    if (!def.trainable) return;
    const home = STRUCTURE_DEFS[def.from[0]];
    if (home.race !== undefined && home.race !== state.race) return;
    // a soldier exists where the hall that trains him does — same rule as the
    // palette, and for the same reason the buildings above are filtered here
    if (!unitInEdition(type as UnitType, editionInPlay())) return;
    let has = false;
    for (const s of state.structures.values()) {
      if (s.owner === state.you && STRUCTURE_DEFS[s.type].trains.includes(type)) { has = true; break; }
    }
    const cost = () => nextUnitCost(type);
    const why = () => {
      const army = armyInfo();
      if (!has) return `you have no ${home.name}`;
      if (army.size >= army.cap) return `your army is full (${army.size} of ${army.cap})`;
      if (!affordable(cost())) return 'you cannot pay for it';
      // people are the one price gold cannot cover: a soldier *is* a citizen
      const pop = nextUnitPop(type);
      if (pop > 0 && state.self.troops < pop) {
        return `you need ${shortNumber(pop)} people at home — you have ${shortNumber(state.self.troops)}`;
      }
      return '';
    };

    items.push({
      icon: def.key,
      name: def.name,
      cost,
      why,
      tip: () => unitTooltip(type) +
        `<div class="t-line">${ico('pop', 11)} ${shortNumber(nextUnitPop(type))} people — `
        + 'they come out of your villages, not out of nowhere</div>' +
        '<div class="t-line">Built in the closest free hall, then marches to this tile.</div>' +
        (why() ? `<div class="t-line t-bad">${why()}</div>` : ''),
      run: () => { net.send({ t: 'train', u: type, x: fx, y: fy }); sfx.play('train'); },
      // land soldiers are ordered in numbers, so the wheel stays open for them
      repeatable: true,
    });
  });

  let harbour = false;
  for (const s of state.structures.values()) {
    if (s.owner === state.you && s.type === StructureType.Harbour) { harbour = true; break; }
  }
  const hostile = !isFriendly(state.owner[tile]);
  const boatWhy = () => !harbour ? 'you have no Harbour'
    : !hostile ? 'you already hold that shore' : '';
  items.push({
    icon: 'harbour',
    name: 'Transport',
    cost: () => null,
    why: boatWhy,
    tip: () => '<h4>Troop transport</h4><div class="t-desc">Sails from your nearest Harbour and lands ' +
      'your attack ratio on this shore.</div>'
      + (boatWhy() ? `<div class="t-line t-bad">${boatWhy()}</div>` : ''),
    run: () => { net.send({ t: 'boat', tile, ratio: state.ratio }); sfx.play('click'); },
  });

  /*
   * What is left of diplomacy: helping the people you were dealt.
   *
   * Alliances cannot be proposed, accepted or broken any more — the only side
   * you have is your team, and the lobby decided that. So the slice is offered
   * for a team-mate only, and all it does is open the two things you can still
   * do for them: send goods, or send men.
   */
  const landOwner = state.owner[tile];
  const other = landOwner !== 0 && landOwner !== state.you ? state.players.get(landOwner) : undefined;
  if (other && other.kind !== PlayerKind.Camp && relationColorTag(landOwner) === 'team') {
    /*
     * Goods, not men. Men are the middle of this wheel now (`buildHub`), so
     * this slice is the *other* half of helping a team-mate and is named after
     * it — two slices both saying "Aid" on one wheel would be a coin flip.
     */
    const caravanWhy = () => {
      if (countOwn(StructureType.Market) === 0) return 'you have no Market to load a caravan';
      const books = state.allyBooks.get(landOwner);
      if (!books || books.markets === 0) return `${other.name} has no Market to unload at`;
      return '';
    };
    items.push({
      icon: 'caravan',
      name: 'Caravan',
      cost: () => null,
      // never greyed out: the panel behind it is also the way to look at them,
      // and a missing Market is worth saying rather than worth refusing over
      why: () => '',
      tip: () => kingdomCard(landOwner) +
        '<div class="t-line">Send a relief caravan of goods — or look over their books.</div>' +
        (caravanWhy() ? `<div class="t-line t-bad">${caravanWhy()}</div>` : ''),
      run: () => openDiplo(landOwner, $('radial')),
    });
  }
  return items;
}

function radialButton(item: RadialItem, angle: number, radius: number, size: number): HTMLElement {
  const btn = document.createElement('button');
  const cost = item.cost();
  btn.className = 'radial-btn' + (item.why() === '' ? '' : ' off');
  btn.style.width = `${size}px`;
  btn.style.height = `${size}px`;
  btn.style.left = `${Math.cos(angle) * radius}px`;
  btn.style.top = `${Math.sin(angle) * radius}px`;
  /*
   * The price is on the button, not only in the tooltip.
   *
   * What a thing costs is the first thing anybody checks, and making them hover
   * every slice to find out turns a one-gesture menu into twenty gestures.
   */
  btn.innerHTML = `${ico(item.icon, Math.round(size * 0.55))}<span class="rl">${item.name}` +
    (cost ? `<span class="rc">${costHtml(cost, 9)}</span>` : '') + '</span>';
  radialLive.push({ item, btn, cost: btn.querySelector('.rc') });
  attachTooltip(btn, item.tip);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    // asked again at the moment of the click, not at the moment it was drawn
    const refused = item.why();
    if (refused) { notice(refused, 'bad'); sfx.play('error'); return; }
    item.run();
    /*
     * **Recruiting leaves the wheel open; everything else closes it.**
     *
     * Ordering one soldier is almost never what a player wants — they want
     * three knights, or five archers — and closing the menu after each meant
     * right-clicking the same tile again for every single one. The wheel
     * re-prices itself live (`refreshRadial`), so it stays honest while it is
     * held open: a slice you can no longer afford dims under your cursor
     * between clicks.
     *
     * Anything that is not a recruit still closes, because those are all
     * one-shot gestures — a building goes down once, a transport sails once, a
     * caravan is dispatched once. `Esc` or a click outside dismisses it.
     */
    if (!item.repeatable) closeRadial();
  });
  return btn;
}

/**
 * Re-prices the open wheel.
 *
 * Called from the HUD tick, so a slice you could not afford lights up the
 * instant the resources land — no closing and re-opening the menu to find out.
 * Only the class and the price text are touched: rebuilding the buttons would
 * throw away the tooltip and the hover state under the cursor.
 */
function refreshRadial() {
  if (radialTile < 0) return;
  for (const row of radialLive) {
    row.btn.classList.toggle('off', row.item.why() !== '');
    const cost = row.item.cost();
    if (row.cost && cost) {
      const html = costHtml(cost, 9);
      if (row.cost.innerHTML !== html) row.cost.innerHTML = html;
    }
  }
}

/**
 * When you last sent people to each team-mate, keyed by their id.
 *
 * A client-side mirror of `Player.lastReinforce` on the server, which remains
 * the only authority — this exists so the middle of the wheel can *say* "in 5s"
 * instead of offering a button that is going to be refused. Deliberately
 * written when the order is sent rather than when it is confirmed: an order
 * that bounced still started the clock as far as the player is concerned, and
 * an optimistic mirror errs towards telling them to wait, never towards
 * promising them a send that will not happen.
 */
const lastReinforceAt = new Map<number, number>();

/** Seconds still owed before this team-mate can take more of your people. */
function reinforceWait(id: number): number {
  const at = lastReinforceAt.get(id);
  if (at === undefined) return 0;
  return Math.max(0, REINFORCE_COOLDOWN - (performance.now() - at) / 1000);
}

/** Remembers a reinforcement, from wherever in the UI it was ordered. */
function noteReinforce(id: number) {
  lastReinforceAt.set(id, performance.now());
}

/**
 * Why you cannot hand this team-mate people this second — '' when you can.
 *
 * Short enough for the middle of the wheel, which is 92 pixels across.
 */
function reinforceWhy(id: number): string {
  if (state.defeated) return 'your kingdom has fallen';
  if (Math.floor(state.self.troops * state.ratio) < 1) return 'nobody to send';
  const wait = reinforceWait(id);
  if (wait > 0) return `wait ${Math.ceil(wait)}s`;
  return '';
}

/**
 * The middle of the wheel is the verb for that tile: **attack** it if it can be
 * attacked, **aid** it if it belongs to a team-mate, and otherwise just say
 * what it is.
 */
function buildHub(tile: number): HTMLElement {
  const owner = state.owner[tile];
  const me = you();
  const terrainName = state.terrain ? TERRAIN_NAME[state.terrain[tile]] : '';
  const ownerName = owner === 0 ? 'wilderness' : (state.players.get(owner)?.name || 'unknown');
  const hub = document.createElement('div');
  hub.className = 'radial-hub';

  const friendly = isFriendly(owner);
  const attackable = owner !== state.you && !friendly
    && (owner === 0 ? true : !state.players.get(owner)?.protectedNow);

  if (attackable) {
    hub.classList.add('attack');
    hub.innerHTML = `<b>Attack</b><span>${escapeHtml(ownerName)}</span>` +
      `<span class="pct">${Math.round(state.ratio * 100)} % of your army</span>`;
    hub.addEventListener('click', (e) => {
      e.stopPropagation();
      net.send({ t: 'attack', tile, ratio: state.ratio });
      sfx.play('attack');
      closeRadial();
    });
    return hub;
  }

  /*
   * **A team-mate gets aid where a stranger gets a spear.**
   *
   * The same gesture, the same slider, the same arithmetic that used to happen
   * on the right-click itself: whatever fraction of your reserve is dialled in
   * marches to their side of the line. Blue rather than red, and a crowd of
   * people rather than a weapon, so that the one slice in the game that hands
   * your army away can never be mistaken for the one that spends it.
   *
   * The relief caravan has not been lost with it — it is the `Caravan` slice
   * out on the ring, and a left click on their ground. Goods are a considered
   * gift with a panel of sliders; men are one number and one click, and only
   * the second one belongs in the middle of a wheel.
   */
  if (owner !== 0 && owner !== state.you && friendly) {
    const why = reinforceWhy(owner);
    const share = Math.floor(state.self.troops * state.ratio);
    hub.classList.add('aid');
    if (why) hub.classList.add('off');
    hub.innerHTML = `<b>${ico('pop', 14)}Aid</b><span>${escapeHtml(ownerName)}</span>` +
      `<span class="pct">${why || `${Math.round(state.ratio * 100)} % of your people (${shortNumber(share)})`}</span>`;
    hub.addEventListener('click', (e) => {
      e.stopPropagation();
      // asked again at the moment of the click, not at the moment it was drawn
      // — the wheel can sit open for longer than the cooldown it is reporting
      const refused = reinforceWhy(owner);
      if (refused) {
        const wait = reinforceWait(owner);
        notice(wait > 0
          ? `${ownerName} can take more of your people in ${Math.ceil(wait)}s.`
          : refused, 'bad');
        sfx.play('error');
        return;
      }
      net.send({ t: 'reinforce', to: owner, amount: Math.floor(state.self.troops * state.ratio) });
      noteReinforce(owner);
      sfx.play('click');
      closeRadial();
    });
    return hub;
  }

  hub.innerHTML = `<b>${escapeHtml(ownerName)}</b><span>${terrainName}</span>`;
  hub.addEventListener('click', (e) => { e.stopPropagation(); closeRadial(); });
  return hub;
}

function countOwn(type: number): number {
  let n = 0;
  for (const s of state.structures.values()) if (s.owner === state.you && s.type === type) n++;
  return n;
}

/* ------------------------------------------------------- aid caravans */

let aidTarget = -1;

/**
 * Pick what goes on the cart. The load leaves your stores at once and walks
 * there — anyone who kills the caravan on the way takes a quarter of it.
 */
export function openAidPanel(toId: number) {
  const p = state.players.get(toId);
  if (!p) return;
  aidTarget = toId;
  const panel = $('aid-panel');
  const rows: string[] = [];
  for (let r = 0; r < RES_COUNT; r++) {
    const have = Math.floor(state.self.res[r]);
    const max = Math.min(AID_MAX_PER_RESOURCE, have);
    rows.push(
      `<div class="aid-row" data-res="${r}">${ico(RES_KEY[r], 14)}` +
      `<input type="range" min="0" max="${max}" value="0" ${max <= 0 ? 'disabled' : ''} />` +
      `<span class="amt">0</span><span class="have">of ${shortNumber(have)}</span></div>`,
    );
  }
  panel.innerHTML =
    `<div class="card-head">Relief caravan to <b style="color:${hexString(p.color)}">${escapeHtml(p.name)}</b></div>` +
    rows.join('') +
    '<div class="aid-note">The load leaves your stores now and travels by road. ' +
    'Whoever kills the caravan takes <b>a quarter</b> of it, and the rest burns.</div>' +
    '<div class="aid-actions"><button id="aid-send" class="primary">Send</button>' +
    '<button id="aid-cancel">Cancel</button></div>';
  panel.classList.remove('hidden');

  const sync = () => {
    let total = 0;
    panel.querySelectorAll('.aid-row').forEach((row) => {
      const input = row.querySelector('input') as HTMLInputElement;
      row.querySelector('.amt')!.textContent = input.value;
      total += Number(input.value);
    });
    ($('aid-send') as HTMLButtonElement).disabled = total < 1;
  };
  panel.querySelectorAll('input').forEach(i => i.addEventListener('input', sync));
  sync();

  $('aid-cancel').addEventListener('click', () => closeAidPanel());
  $('aid-send').addEventListener('click', () => {
    const amounts = new Array(RES_COUNT).fill(0);
    panel.querySelectorAll('.aid-row').forEach((row) => {
      const r = Number((row as HTMLElement).dataset.res);
      amounts[r] = Number((row.querySelector('input') as HTMLInputElement).value);
    });
    net.send({ t: 'aid', to: aidTarget, amounts });
    sfx.play('coin');
    closeAidPanel();
  });
}

export function closeAidPanel() {
  aidTarget = -1;
  $('aid-panel').classList.add('hidden');
}

export const aidPanelOpen = () => aidTarget >= 0;

/** Opens the wheel on a tile, anchored at the click but kept on screen. */
export function openRadial(tile: number, sx: number, sy: number) {
  if (state.phase !== Phase.Live || state.defeated) return;
  radialOpenedAt = performance.now();
  closeAidPanel();
  const el = $('radial');
  radialTile = tile;
  radialLive = [];

  const units = trainRadialItems(tile);
  const builds = buildRadialItems(tile);
  // wider than it was, because every slice now carries its price under it
  const outerR = 142;
  /*
   * Clamped to the *page*, not to `window.innerWidth`. Under a pinch the window
   * reports the slice of the page you can see, and a wheel clamped to that is a
   * wheel jammed into whichever corner you happened to be looking at.
   */
  const cx = Math.max(outerR + 46, Math.min(pageW() - outerR - 46, sx));
  const cy = Math.max(outerR + 46, Math.min(pageH() - outerR - 46, sy));
  el.style.left = `${cx}px`;
  el.style.top = `${cy}px`;
  el.innerHTML = '';

  el.appendChild(buildHub(tile));

  units.forEach((item, i) => {
    const a = -Math.PI / 2 + (i / units.length) * Math.PI * 2;
    el.appendChild(radialButton(item, a, 76, 42));
  });
  builds.forEach((item, i) => {
    const a = -Math.PI / 2 + ((i + 0.5) / builds.length) * Math.PI * 2;
    el.appendChild(radialButton(item, a, outerR, 40));
  });

  el.classList.remove('hidden');
  sfx.play('click');
}

function renderPingRow() {
  for (const btn of document.querySelectorAll('#ping-row button')) {
    const el = btn as HTMLElement;
    const kind = Number(el.dataset.ping);
    el.classList.toggle('on', kind === pingMode);
    /*
     * Each button wears the colour of the mark it drops. Set from the same
     * table the renderer reads (`PING_COLOR`), so the two can never drift into
     * a button that promises one colour and produces another.
     */
    const col = hexString(PING_COLOR[kind] ?? 0xffffff);
    el.style.setProperty('--ping', col);
  }
}

/* ----------------------------------------------------------------- HUD */

export function updateHUD() {
  renderResources();

  const phaseNames = ['LOBBY', 'CHOOSE YOUR HOMELAND', 'WAR', 'RESULTS'];
  $('phase-label').textContent = phaseNames[state.phase] || '';
  $('phase-timer').textContent = formatTime(state.countdown);
  $('rtt').textContent = `${Math.round(net.rtt)} ms`;
  renderWinTrack();

  /*
   * **The bar is the reserve — the people still at home.**
   *
   * It used to add the marching army back in, which made it a picture of how
   * many soldiers you own rather than how strong you are, and those are
   * different things at the only moment it matters: sending thirty thousand
   * men over the border left the bar exactly where it was, so the one thing
   * the HUD should have shouted — *your country is empty, and anybody can see
   * it* — was the one thing it hid. The marching men are still named in the
   * text beside it; they are simply not counted as defence they are not.
   */
  const cap = Math.max(1, state.self.cap);
  ($('troop-bar') as HTMLElement).title =
    'The people still at home — what is left to hold your ground and to attack with. '
    + `Soldiers away over the border are not counted. Recruitment is fastest at ${Math.round(GROWTH_PEAK_AT * 100)} % `
    + 'of capacity and tails off as the country fills up.';
  ($('troop-fill') as HTMLElement).style.width = `${Math.min(100, (state.self.troops / cap) * 100)}%`;
  $('troop-text').innerHTML =
    `${ico('troops', 12)} ${shortNumber(state.self.troops)}` +
    `${state.self.committed > 1 ? ` <span style="color:#f0c95a">+${shortNumber(state.self.committed)} marching</span>` : ''}` +
    ` / ${shortNumber(cap)}` +
    ` <span class="rate">+${state.self.troopRate.toFixed(1)}/s${state.self.villageBonus > 0.001
      ? ` <em>villages +${Math.round(state.self.villageBonus * 100)} %</em>` : ''}` +
    `<em class="${state.self.foodFactor < 0.7 ? 'lean' : ''}"> larder ×${state.self.foodFactor.toFixed(2)}</em></span>`;

  const army = armyInfo();
  $('army-count').textContent = `${army.size}/${army.cap}`;
  ($('army-count') as HTMLElement).title =
    'Your halls decide how many soldiers you may field. Every building that trains units adds one, and one more per level.';

  const me = you();
  $('kingdom-count').textContent = me ? `${shortNumber(me.tiles)} tiles` : '';
  if (me && me.tiles > state.peakTiles) state.peakTiles = me.tiles;
  renderLadder();

  renderBuildTerrainNote();
  renderRealmRow();
  renderAttackList();
  renderSquadRow();
  refreshPalettes();
  renderTeams();
  refreshRadial();
  renderHoverCard();
  renderDefeat();
  renderWatchBar();
  // the defeat panel raises and lowers itself from inside renderDefeat, so the
  // ad frame follows it from here rather than from a click handler
  syncAds(adSurfaceVisible());

  if (state.defeated) {
    setHint(state.spectating ? 'Spectating — the war goes on without you' : '');
    return;
  }
  if (state.powerMode) {
    const def = RACE_POWERS[state.race] || RACE_POWERS[0];
    setHint(`${def.verb} — click where it should fall\n`
      + `scroll to size the blast · ${state.powerRadius} tiles wide · Esc to think better of it`);
  } else if (state.wallMode !== 0) {
    setHint(state.wallMode === WallKind.Gate
      ? 'Drag across your own land to set gates\nonly you and your allies pass through them'
      : 'Drag across your own land to draw a wall\nno army takes ground through it — only units break it');
  } else if (state.buildType >= 0) {
    setHint(`Click your land to place a ${STRUCTURE_DEFS[state.buildType].name}`);
  } else if (state.trainType >= 0) {
    setHint(`Click where the ${UNIT_DEFS[state.trainType].name} should march\nit is built in the closest free building`);
  } else if (state.boatMode) {
    setHint('Click a shore to land troops there\nor open water to sail out and wait');
  } else if (state.phase === Phase.Live) {
    setHint('');
  }
}

/**
 * Only the five resources. There is no separate citizen counter any more:
 * **your people are your army**, and that single number lives in the troop bar
 * where it belongs.
 */
function renderResources() {
  const el = $('resources');
  if (el.children.length !== RES_COUNT) {
    el.innerHTML = '';
    for (let r = 0; r < RES_COUNT; r++) {
      const d = document.createElement('div');
      d.className = 'res';
      d.innerHTML = `${ico(RES_KEY[r], 16)}<span class="col"><span class="amt">0</span><span class="inc">+0</span></span>`;
      attachTooltip(d, () => resourceTooltip(r));
      el.appendChild(d);
    }
  }
  for (let r = 0; r < RES_COUNT; r++) {
    const item = el.children[r] as HTMLElement;
    /*
     * Mana is hidden rather than removed in a Light world.
     *
     * The array is `RES_COUNT` wide everywhere — the protocol, the price
     * tables, the economy loop — and narrowing it for one edition would mean
     * two shapes of everything. There simply is no mana to show: no ley fields
     * on the map and no Ley Shrine to build, so the pill would read a flat zero
     * forever and teach a beginner about a resource that is not in their game.
     */
    item.classList.toggle('hidden', r === Res.Mana && !manaInEdition(editionInPlay()));
    item.querySelector('.amt')!.textContent = shortNumber(state.self.res[r]);
    item.querySelector('.inc')!.textContent = `+${state.self.income[r].toFixed(1)}/s`;
    item.classList.toggle('low', r === 0 && state.self.starving);
  }
}

function resourceTooltip(r: number): string {
  const sources: Record<number, string> = {
    0: 'Plains produce the most food. Feeds citizens and soldiers — running out starves both.',
    1: 'Forest produces the most wood. Used by almost every building.',
    2: 'Mountains produce the most stone. Knights, towers and roosts need it.',
    3: 'Mana never comes from land. A Ley Shrine on a ley field is the only source, and gold cannot buy it.',
    4: 'Gold never comes from land: your people pay taxes, and caravans between markets carry it.',
  };
  const larder = r === Res.Food
    ? `<div class="t-line ${state.self.foodFactor < 0.7 ? 't-bad' : 't-good'}">A deep granary breeds people: ` +
      `recruitment runs at <b>×${state.self.foodFactor.toFixed(2)}</b> right now, and tops out with ` +
      `${FOOD_COMFORT_SECONDS} seconds of eating in store (at most ${FOOD_COMFORT_MAX}).</div>`
    : '';
  return `<h4>${RES_NAME[r]}</h4><div class="t-desc">${sources[r]}</div>` +
    `<div class="t-line">Income ${state.self.income[r].toFixed(2)}/s</div>` + larder +
    (r === Res.Gold
      ? '<div class="t-line t-good">Gold is a joker: 1 gold buys 1 food, wood or stone, and is spent automatically whenever you are short.</div>'
      : '<div class="t-line">Every land tile makes a little of this — 20 % of what its own terrain makes. Ley fields make 60 % of everything.</div>');
}

function renderBuildTerrainNote() {
  const note = document.getElementById('build-terrain');
  if (!note) return;                       // no palette on a touch screen
  if (state.buildType < 0 || state.hoverTile < 0 || !state.terrain) { note.textContent = ''; return; }
  const def = STRUCTURE_DEFS[state.buildType];
  const terrain = state.terrain[state.hoverTile];
  const f = terrainFactor(def, terrain, state.race);
  note.textContent = `${TERRAIN_NAME[terrain]} · ${Math.round(f * 100)}%`;
  (note as HTMLElement).style.color = f >= 1 ? 'var(--good)' : 'var(--bad)';
}

/**
 * There is no clock: the bar shows how close the leading team is to the line.
 *
 * The arithmetic has to be the server's arithmetic exactly — see
 * `contestedLand` — or the bar reads a number that never ends the match. Two
 * things are written off: the wilderness the monster camps hold, and every tile
 * under a wall or a gate.
 */
function renderWinTrack() {
  const track = $('win-track');
  const note = $('win-note');
  if (state.phase !== Phase.Live) {
    track.classList.add('hidden');
    note.textContent = '';
    return;
  }
  track.classList.remove('hidden');
  /*
   * The server's own arithmetic, not a sum over what happens to be on screen.
   *
   * Per-kingdom stats stopped covering the whole world when they became a
   * viewport question, and adding up a subset would have quietly read "31 %"
   * through a match somebody was winning. These are the numbers `checkVictory`
   * itself runs on, so the bar and the result can no longer disagree.
   */
  let bestTeam = -1, bestTiles = 0;
  for (const [team, tiles] of state.teamLand) if (tiles > bestTiles) { bestTiles = tiles; bestTeam = team; }
  const share = bestTiles / Math.max(1, state.contestedLand);
  ($('win-bar') as HTMLElement).style.width = `${Math.min(100, (share / TEAM_WIN_SHARE) * 100)}%`;
  note.textContent = bestTeam < 0
    ? `hold ${Math.round(TEAM_WIN_SHARE * 100)} % of the land to win`
    : `${TEAM_DEFS[bestTeam % TEAM_DEFS.length].name} ${(share * 100).toFixed(1)} % of ${Math.round(TEAM_WIN_SHARE * 100)} %`;
}

/** How many rows the ladder shows before it stops being a glance. */
const LADDER_ROWS = 12;

/**
 * **The kingdom ladder, and the way to get to a kingdom.**
 *
 * The list itself is the smaller half of what this is for. The map already
 * writes every realm's name across its own ground — but only once that ground
 * is big enough on screen to carry a name, which means the kingdoms you most
 * want to find, the small ones, are exactly the ones the map will not tell you
 * about. A row here is a way to travel to one.
 *
 * Ranked on land, because land is what wins: `TEAM_WIN_SHARE` of the contested
 * world ends the match, so the tile count *is* the score. Troops are shown
 * beside it because the two together are the whole question — a big realm with
 * no army is a meal, and a small one with a full one is a problem.
 *
 * Camps are left out. They hold ground and have names, but they are scenery
 * with loot in it, not contenders, and a dozen of them would push the kingdoms
 * that matter off the bottom.
 */
function renderLadder() {
  const el = $('ladder');
  const mine = state.you;
  const all = [...state.players.values()]
    .filter(p => !p.eliminated && p.kind !== PlayerKind.Camp && p.tiles > 0)
    .sort((a, b) => b.tiles - a.tiles);

  $('ladder-note').textContent = all.length ? `${all.length} alive` : '';
  if (!all.length) { el.innerHTML = '<div class="ladder-empty">No kingdoms yet.</div>'; return; }

  const total = all.reduce((n, p) => n + p.tiles, 0) || 1;
  const top = all.slice(0, LADDER_ROWS);
  /*
   * You are always on this list. Falling off the bottom of it is precisely the
   * moment you most want to know where you stand, and a ladder that quietly
   * stops mentioning you when you are losing is answering the wrong question.
   */
  const meRank = all.findIndex(p => p.id === mine);
  if (meRank >= LADDER_ROWS) top.push(all[meRank]);

  el.innerHTML = top.map((p) => {
    const rank = all.indexOf(p) + 1;
    const share = (p.tiles / total) * 100;
    const self = p.id === mine ? ' self' : '';
    return `<button class="ladder-row${self}" data-focus="${p.id}" `
      + `title="Fly to ${escapeHtml(p.name)} — ${shortNumber(p.tiles)} tiles, ${shortNumber(p.troops)} people">`
      + `<span class="lr-rank">${rank}</span>`
      + `<i class="lr-dot" style="background:${hexString(p.color)}"></i>`
      + `<span class="lr-name">${escapeHtml(p.name)}</span>`
      + `<span class="lr-pts">${shortNumber(p.tiles)}</span>`
      + `<span class="lr-share">${share < 10 ? share.toFixed(1) : Math.round(share)} %</span>`
      + '</button>';
  }).join('');

  for (const row of el.querySelectorAll<HTMLElement>('.ladder-row')) {
    row.addEventListener('click', () => {
      focusKingdom(Number(row.dataset.focus));
      sfx.play('click');
    });
  }
}

function renderTeams() {
  const el = $('team-list');
  // land from the server's standings (see renderWinTrack); heads from the
  // roster, which every client holds in full because it is who is in the match
  const heads = new Map<number, number>();
  for (const p of state.players.values()) {
    if (p.team < 0) continue;
    heads.set(p.team, (heads.get(p.team) || 0) + 1);
  }
  const me = you();
  const rows = [...state.teamLand.entries()].sort((a, b) => b[1] - a[1]);
  el.innerHTML = '';
  for (const [team, tiles] of rows) {
    const div = document.createElement('div');
    div.className = 'row' + (me && me.team === team ? ' me' : '');
    const color = hexString(teamHex(team));
    div.innerHTML =
      `<span class="chip" style="background:${color};color:${color}"></span>` +
      `<span class="nm">${TEAM_DEFS[team % TEAM_DEFS.length].name} <small>${heads.get(team) || 0}</small></span>` +
      `<span class="val">${shortNumber(tiles)}</span>`;
    el.appendChild(div);
  }
}

function teamHex(team: number): number {
  for (const p of state.players.values()) if (p.team === team) return p.color;
  return 0x999999;
}

/**
 * How many levels of one kind of building you hold, counted off your own
 * structures.
 *
 * The client is not told anybody's `structLevels`, but it does hold every
 * building it can see — and your own are always sent. That is enough for the
 * two places this is wanted: what a new one of these would produce for you, and
 * what one you are hovering over is producing.
 */
function myLevelsOf(key: string): number {
  let n = 0;
  for (const s of state.structures.values()) {
    if (s.owner !== state.you) continue;
    if (STRUCTURE_DEFS[s.type]?.key === key) n += s.level;
  }
  return n;
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] || c));
}

/* ------------------------------------------------------------ diplomacy */

/**
 * What you can do about another kingdom — which, since alliances were removed,
 * is: help a team-mate, or go and look at them.
 */
function openDiplo(id: number, anchor: HTMLElement) {
  const p = state.players.get(id);
  const popup = $('diplo-popup');
  if (!p || id === state.you) { popup.classList.add('hidden'); return; }
  const allied = relationColorTag(id) === 'team';

  let html = `<h4 style="color:${hexString(p.color)}">${escapeHtml(p.name)}</h4>`;
  html += `<div class="sub">${TEAM_DEFS[p.team % TEAM_DEFS.length]?.name || '—'} team · ` +
    `${shortNumber(p.tiles)} tiles · ${shortNumber(p.troops)} / ${shortNumber(p.troopCap)} troops</div>`;

  if (allied) {
    html += `<button data-aid="1">${ico('caravan', 12)} Send a relief caravan</button>`;
    // the same fraction the attack slider is set to, so this button and a
    // right-click on their ground are visibly the same act
    const share = Math.round(state.ratio * 100);
    html += `<button data-reinforce="1">${ico('troops', 12)} Send ${share} % of your people ` +
      `(${shortNumber(Math.floor(state.self.troops * state.ratio))})</button>`;
  } else {
    html += `<div class="sub">${atWarWith(id)
      ? 'At war with you. Your soldiers will engage theirs on sight.'
      : 'No quarrel with you. Your soldiers will leave theirs alone until one of you strikes.'}</div>`;
  }
  html += '<button data-focus="1">Look at their lands</button>';
  popup.innerHTML = html;

  // anchored wherever it was opened from — the wheel sits anywhere on screen,
  // so a panel pinned to the old right-hand column would often land off it
  const rect = anchor.getBoundingClientRect();
  popup.style.right = 'auto';
  popup.style.left = `${Math.max(8, Math.min(pageW() - 250, rect.left + 24))}px`;
  popup.style.top = `${Math.max(8, Math.min(pageH() - 250, rect.top))}px`;
  popup.classList.remove('hidden');

  popup.querySelectorAll('button').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const d = (btn as HTMLElement).dataset;
      if (d.aid) openAidPanel(id);
      else if (d.reinforce) {
        // the same book-keeping the wheel does, so whichever door the order
        // went through, the other one knows the clock has started
        net.send({ t: 'reinforce', to: id, amount: Math.floor(state.self.troops * state.ratio) });
        noteReinforce(id);
      } else if (d.focus) focusKingdom(id);
      popup.classList.add('hidden');
    });
  });
}

/* --------------------------------------------------------------- feeds */

export function notice(text: string, kind?: string) {
  const box = $('notices');
  const div = document.createElement('div');
  div.className = 'notice' + (kind ? ` ${kind}` : '');
  div.textContent = text;
  box.appendChild(div);
  setTimeout(() => div.remove(), 5200);
  while (box.children.length > 6) box.firstChild?.remove();
}

export function setHint(text: string) {
  $('hint').textContent = text;
}

/* --------------------------------------------------------------- lobby */

export function updateLobby() {
  const info = state.lobby;
  refreshRacePickers();

  /*
   * A private room has no clock. Showing a frozen "25" to a host who is waiting
   * for their friends would be a countdown that never counts, so the timer slot
   * carries the room state instead and the host gets a button.
   */
  const youAreHost = info.manualStart && info.hostId === state.you && state.you !== 0;
  $('lobby-timer').textContent = info.manualStart
    ? '—' : String(Math.max(0, Math.ceil(state.countdown)));
  $('lobby-start').classList.toggle('hidden', !youAreHost);
  $('lobby-note').textContent = info.manualStart
    ? (youAreHost
      ? 'This room is yours. It will wait as long as you like — start it when everyone has arrived.'
      : 'Waiting for the host to start the war. Grey AI kingdoms and monster camps join every match.')
    : 'The match starts when the countdown ends — or instantly when the room fills up. '
      + 'Teamless grey AI kingdoms and orc/goblin camps join every match.';

  $('lobby-room').textContent = state.roomId;
  /*
   * Hand the room code to the portal, which turns it into an invite link and a
   * button of its own. The code is already the game's own sharing mechanism —
   * `?room=CODE` in the menu — so nothing new had to be invented for this; the
   * portal's flow and ours are the same idea reached from two directions.
   */
  if (ON_CRAZYGAMES) portal.setRoom(state.roomId);
  $('lobby-count').textContent = `${info.humans} / ${info.max}`;
  $('lobby-teams').textContent = info.mode === RoomMode.FreeForAll ? 'free-for-all'
    : info.mode === RoomMode.VersusAi ? 'all of you vs all of them'
      : `${info.teams} × ${info.perTeam}`;
  // in VS AI the machine count is the enemy army, so it deserves saying plainly
  $('lobby-ai').textContent = info.mode === RoomMode.VersusAi
    ? `${info.aiKingdoms} (the enemy)` : String(info.aiKingdoms);
  $('lobby-camps').textContent = String(info.camps);
  const worldEl = document.getElementById('lobby-world');
  // the world is sized to the roster, so it grows while people are still joining
  if (worldEl) worldEl.textContent = `${info.worldW} × ${info.worldH}`;
  ($('lobby-fill-bar') as HTMLElement).style.width = `${Math.min(100, (info.humans / Math.max(1, info.max)) * 100)}%`;

  renderLobbyTeams();

  const box = $('lobby-players');
  box.innerHTML = '';
  for (const p of state.players.values()) {
    if (p.kind !== PlayerKind.Human) continue;
    const d = document.createElement('div');
    // the host is marked, so everyone knows who they are waiting on
    const mark = p.id === state.you ? '★ ' : '';
    // the people they have picked, in brackets — it is still changeable in here,
    // so seeing everyone's choice is part of deciding your own
    const people = RACE_DEFS[p.race]?.name ?? '';
    d.textContent = mark + p.name + (people ? ` (${people})` : '')
      + (info.manualStart && p.id === info.hostId ? ' · host' : '');
    // and whoever has picked a side wears it
    if (info.mode === RoomMode.Teams && p.team >= 0) {
      d.style.borderLeft = `3px solid ${hexString(teamHex(p.team))}`;
      d.style.paddingLeft = '7px';
    }
    box.appendChild(d);
  }
}

/**
 * Choosing who you stand with — **in your own room only.**
 *
 * Only in a team world at all: a free-for-all has nothing to choose and VS AI
 * already put everybody on the same side.
 *
 * And only in a private one. A public world is a queue of strangers, and in a
 * queue "pick a side" is not a choice, it is a race: everyone piles onto the
 * side that is winning or onto the side their friend took, and the war opens
 * nine against three. Sides there are dealt by the server, evenly, and the only
 * honest thing the lobby can do is say so. A room somebody opened with a code
 * is the opposite case — the people in it arrived together and arranging
 * themselves is the whole reason it exists — so the buttons are theirs.
 *
 * `manualStart` is the flag for that, and it is the same one everywhere else in
 * the client: it is what makes the countdown a Start button and what keeps a
 * room off the front page. **The server refuses a `team` message from a public
 * room regardless** — this only means nobody is offered a button that is about
 * to be ignored.
 */
function renderLobbyTeams() {
  const info = state.lobby;
  const teamWorld = info.mode === RoomMode.Teams && state.phase === Phase.Lobby;
  const canPick = teamWorld && info.manualStart;
  $('lobby-team-head').classList.toggle('hidden', !teamWorld);
  $('lobby-team-head').querySelector('span')!.textContent = canPick ? 'Pick a side' : 'Sides';
  const box = $('lobby-team-pick');
  box.classList.toggle('hidden', !teamWorld);
  // the strip explains instead of offering, and stops being a button grid
  box.classList.toggle('auto', !canPick);
  if (!teamWorld) return;

  if (!canPick) {
    const line = 'Teams are drawn up automatically here, and kept even. '
      + 'Open a room of your own if you want to choose your side.';
    if (box.dataset.shape !== 'auto') {
      box.dataset.shape = 'auto';
      box.innerHTML = `<div class="muted">${line}</div>`;
    }
    return;
  }

  const mine = you()?.team ?? -1;
  const wanted = `${info.teams}:${mine}`;
  if (box.dataset.shape !== wanted) {
    box.dataset.shape = wanted;
    box.innerHTML = '';
    const counts = new Map<number, number>();
    for (const p of state.players.values()) {
      if (p.kind !== PlayerKind.Human || p.team < 0) continue;
      counts.set(p.team, (counts.get(p.team) || 0) + 1);
    }
    for (let t = 0; t < info.teams; t++) {
      const btn = document.createElement('button');
      btn.className = 'team-btn' + (mine === t ? ' on' : '');
      const def = TEAM_DEFS[t % TEAM_DEFS.length];
      btn.innerHTML = `<span class="chip" style="background:${hexString(teamHex(t))}"></span>` +
        `<span>${def.name}</span><small>${counts.get(t) || 0}</small>`;
      btn.addEventListener('click', () => { net.send({ t: 'team', team: t }); sfx.play('click'); });
      box.appendChild(btn);
    }
    const auto = document.createElement('button');
    auto.className = 'team-btn' + (mine < 0 ? ' on' : '');
    auto.innerHTML = '<span>Leave it to the draw</span>';
    auto.addEventListener('click', () => { net.send({ t: 'team', team: -1 }); sfx.play('click'); });
    box.appendChild(auto);
  }
}

export function initLobbyStart() {
  $('lobby-start').addEventListener('click', () => {
    net.send({ t: 'start' });
    sfx.play('click');
  });
  // walking out of a lobby is walking out of the world: the socket closes, and
  // `onClose` puts you back in the menu with the browser polling again
  $('lobby-leave').addEventListener('click', () => {
    sfx.play('click');
    net.disconnect();
  });
}

/* -------------------------------------------------------------- defeat */

/**
 * Losing your last tile does not end the war — it ends *your* war. Only you see
 * this, and you choose: walk away, or stay and watch how it finishes.
 */
function renderDefeat() {
  const box = $('defeat');
  const show = state.defeated && !state.spectating;
  box.classList.toggle('hidden', !show);
  document.body.classList.toggle('spectating', state.defeated && state.spectating);
  if (!show) {
    if (!state.defeated) $('defeat-line').dataset.done = '';
    return;
  }

  const me = you();
  const line = $('defeat-line');
  if (line.dataset.done !== '1') {
    line.dataset.done = '1';
    line.textContent = me
      ? `${me.name} held ${shortNumber(state.peakTiles)} tiles at its height. The war is not over — it just went on without you.`
      : 'The war is not over — it just went on without you.';
  }
}

export function initDefeat() {
  const watch = () => {
    state.spectating = true;
    state.selected.clear();
    state.buildType = -1;
    state.trainType = -1;
    state.boatMode = false;
    closeRadial();
    sfx.play('click');
  };
  $('defeat-watch').addEventListener('click', watch);
  $('defeat-watch-2').addEventListener('click', watch);
  $('defeat-leave').addEventListener('click', () => {
    sfx.play('click');
    net.disconnect();
  });
}

/**
 * The war is over — and that is an announcement, not an eviction.
 *
 * The world stays on screen behind this panel, and stays there for as long as
 * anybody wants to look at it: dismissing this leaves you flying over the
 * finished map. **Nothing starts another war from here** — a war begins in a
 * lobby and nowhere else, so the way on is back to the menu.
 */
export function showResults(msg: { standings: { team: number; tiles: number; name: string }[]; winner: number }) {
  const list = $('result-list');
  list.innerHTML = '';
  const me = you();
  const won = !!me && me.team === msg.winner;
  $('result-title').textContent = won ? 'Your team rules Kaelmoor' : `${msg.standings[0]?.name || '—'} team wins`;
  for (const s of msg.standings) {
    if (s.tiles <= 0 && s.team !== msg.winner) continue;
    const row = document.createElement('div');
    row.className = 'result-row' + (s.team === msg.winner ? ' win' : '');
    row.innerHTML =
      `<span class="chip" style="background:${hexString(teamHex(s.team))}"></span>` +
      `<b style="flex:1">${s.name}</b><span>${shortNumber(s.tiles)} tiles</span>`;
    list.appendChild(row);
  }
  $('results').classList.remove('hidden');
  $('result-reopen').classList.add('hidden');
  // from here on the map is a museum, and the door out has to stay open
  warOver = true;
  renderWatchBar();
  syncAds(adSurfaceVisible());
  sfx.play(won ? 'capture' : 'notice');
}

/** Puts the standings away without leaving the world. */
function dismissResults() {
  $('results').classList.add('hidden');
  $('result-reopen').classList.remove('hidden');
  // back to looking at the finished map, so the frame comes down with the panel
  syncAds(adSurfaceVisible());
  sfx.play('click');
}

/**
 * The two standing orders for the realm.
 *
 * `Defend` is the order the ground-control rule has been asking for since units
 * stopped stacking: spread, they hold many times more border than they do in a
 * heap, and doing that by hand every time the front moves is a chore nobody will
 * keep up with. `Repair` runs until it is stopped, so it is drawn as *working*
 * rather than merely pressed.
 */
function renderRealmRow() {
  /*
   * **Three standing orders, and on a phone they are three words.**
   *
   * These buttons stretched across the whole width of a 375-pixel screen for
   * the sake of "Repairing" and "Runic Colossus" — a third of the map spent on
   * text nobody reads twice. On a touch screen each one shrinks to its own
   * content and says the shortest true thing: the state it is in, in one word,
   * beside an icon that is doing most of the work anyway. The full names are
   * still on the desktop, where there is room for them.
   */
  const sz = IS_TOUCH ? 13 : 15;
  const defend = $('defend-btn');
  const holding = state.self.defending;
  defend.classList.toggle('on', holding);
  defend.innerHTML = `${ico('troops', sz)}<span>${holding ? 'Holding' : 'Defend'}</span>`;
  const repair = $('repair-btn');
  const on = state.self.repairing;
  repair.classList.toggle('on', on);
  // still switched on, but there is nothing to pay the masons with
  repair.classList.toggle('broke', on && state.self.res[Res.Gold] < 1);
  repair.innerHTML = `${ico('hammer', sz)}<span>`
    + `${on ? (IS_TOUCH ? 'Mending' : 'Repairing') : 'Repair'}</span>`;

  const power = $('power-btn');
  const def = RACE_POWERS[state.race] || RACE_POWERS[0];
  const cost = racePowerCost(state.self.powerUses);
  const can = affordable(cost);
  power.classList.toggle('cant', !can);
  power.classList.toggle('armed', state.powerMode);
  const powerLabel = state.powerMode
    ? (IS_TOUCH ? 'Aiming' : 'Choose a target')
    : (IS_TOUCH ? def.short : def.name);
  power.innerHTML = `${ico(def.key === 'colossus' ? 'golem' : def.key === 'wrath' ? 'moongrove' : 'dragon', sz)}` +
    `<span>${powerLabel}</span>`;

  // the way out, for a screen with no Escape key
  const drop = $('drop-tool');
  const showDrop = IS_TOUCH && toolInHand();
  drop.classList.toggle('hidden', !showDrop);
  if (showDrop && !drop.innerHTML) drop.innerHTML = '<span>✕ Drop</span>';
  // the panel below says all of this properly; a native tooltip on top of it
  // would only be a second, worse copy fighting for the same corner
  (power as HTMLElement).title = '';
}

/**
 * What thirty-six thousand actually buys.
 *
 * The most expensive button in the game had nothing behind it but a one-line
 * browser tooltip, which is no way to sell a decision that costs six Warehouse
 * levels to make. Price first, like every other price in the HUD, then what it
 * does — and the figures go red the moment they are what is stopping you.
 */
function powerTooltip(): string {
  const def = RACE_POWERS[state.race] || RACE_POWERS[0];
  const cost = racePowerCost(state.self.powerUses);
  const uses = state.self.powerUses;

  let html = `<h4>${def.name}</h4>`;
  html += `<div class="t-price">${costHtml(cost, 14)}</div>`;
  html += uses > 0
    ? `<div class="t-line">Calling number <b>${uses + 1}</b> — each one costs `
      + `${Math.round(RACE_POWER_STEP * 100)} % more than the last</div>`
    : `<div class="t-line">Every calling after this costs ${Math.round(RACE_POWER_STEP * 100)} % more</div>`;
  html += `<div class="t-desc">${def.desc}</div>`;

  html += `<div class="t-line">Lasts <b>${def.seconds}s</b> and throws `
    + `<b>${racePowerShots(def)}</b> strikes, one every ${def.interval}s</div>`;
  html += `<div class="t-line">Each strike breaks everything hostile within <b>${def.strike}</b> tiles</div>`;
  html += def.walk > 0
    ? `<div class="t-line t-good">It <b>walks</b> — ${def.walk} tiles a second, hunting across `
      + 'the ring you set. The only one that comes to them.</div>'
    : '<div class="t-line">Falls anywhere inside the ring you set</div>';
  /*
   * The one thing that is genuinely a *decision* here, so it gets its own line
   * rather than a footnote: the shot count never changes, only the spread.
   */
  html += `<div class="t-big">Scroll to size the blast — ${RACE_POWER_AREA_MIN} to `
    + `${RACE_POWER_AREA_MAX} tiles<small> · always ${racePowerShots(def)} shots, however wide. `
    + 'Tight is a crater; at full stretch it covers half a continent and scatters</small></div>';
  html += `<div class="t-line">Reach: ${RACE_POWER_RANGE} tiles from the middle of your realm</div>`;
  html += '<div class="t-line t-good">Never harms your own land or your team-mates\'</div>';
  html += '<div class="t-line t-good">Ground it takes goes back to the wilderness — '
    + '<b>walls and all</b>. It is the one thing that opens a fortified border.</div>';

  if (!affordable(cost)) {
    html += '<div class="t-line t-bad">You cannot pay for it — the red figures are what you are short of. '
      + 'Warehouses raise how much you can hold at all.</div>';
  }
  return html;
}

export function initRealmRow() {
  attachTooltip($('power-btn'), () => powerTooltip());
  $('drop-tool').addEventListener('click', () => {
    dropTool();
    renderRealmRow();
    sfx.play('click');
  });
  $('defend-btn').addEventListener('click', () => {
    const on = !state.self.defending;
    // answer the click now; the next snapshot confirms it
    state.self.defending = on;
    net.send({ t: 'defend', on });
    renderRealmRow();
    sfx.play('click');
  });
  $('repair-btn').addEventListener('click', () => {
    const on = !state.self.repairing;
    // answer the click now; the next snapshot confirms it
    state.self.repairing = on;
    net.send({ t: 'repair', on });
    renderRealmRow();
    sfx.play('build');
  });
  $('power-btn').addEventListener('click', () => {
    const cost = racePowerCost(state.self.powerUses);
    if (!affordable(cost)) {
      const def = RACE_POWERS[state.race] || RACE_POWERS[0];
      notice(`${def.name} costs ${cost[Res.Wood]} wood, ${cost[Res.Stone]} stone and ` +
        `${cost[Res.Gold]} gold — build Warehouses if you cannot even hold that much.`, 'bad');
      sfx.play('error');
      return;
    }
    // arming it, not firing it: the next click on the map chooses where
    state.powerMode = !state.powerMode;
    state.buildType = -1;
    state.trainType = -1;
    state.boatMode = false;
    state.wallMode = 0;
    renderRealmRow();
    sfx.play('click');
  });
}

export function initSquadRow() {
  $('squad-spare').addEventListener('click', () => toggleSpareBuildings());
  $('squad-disband').addEventListener('click', () => {
    // everything in hand, traders included — see `selectedOwn`
    const ids = selectedOwn();
    if (ids.length === 0) return;
    net.send({ t: 'disband', ids });
    state.selected.clear();
    sfx.play('click');
  });
}

export function initResults() {
  const dismiss = () => dismissResults();
  $('result-x').addEventListener('click', dismiss);
  $('result-watch').addEventListener('click', dismiss);
  $('result-reopen').addEventListener('click', () => {
    $('results').classList.remove('hidden');
    $('result-reopen').classList.add('hidden');
    syncAds(adSurfaceVisible());
    sfx.play('click');
  });
  $('result-leave').addEventListener('click', () => {
    sfx.play('click');
    net.disconnect();
  });
  // the one that survives every dismissed panel
  $('leave-match').addEventListener('click', () => {
    sfx.play('click');
    net.disconnect();
  });
}
